import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic,
  Monitor,
  Square,
  Play,
  RefreshCw,
  AlertCircle,
  MessageSquare,
  Clock,
  ExternalLink,
  BookOpen,
  Globe,
  CheckCircle,
  Info,
  Copy,
  StopCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  Lightbulb,
  Sparkles,
  Trash2,
  Building2,
  Users,
  Target,
  Cpu,
  Share2,
  GitBranch,
  Award,
  Zap,
} from "lucide-react";
import MeetingIdModal from "./MeetingIdModal";
import EngineeringPracticesPanel from "./EngineeringPracticesPanel";
import RelevantCardsPanel from "./RelevantCardsPanel";
import { apiService } from "../services/apiService";
import webAuthService from "../services/webAuthService";
import { setMeetingActive } from "../services/meetingSession";
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels";
import { usePanelLayout } from "../hooks/usePanelLayout";
import { LayoutGrid, Eye, EyeOff, Layers } from "lucide-react";

/**
 * ScreenCaptureWeb - Web Application Version
 *
 * Uses browser Web APIs for media capture:
 * - navigator.mediaDevices.getDisplayMedia() for screen capture
 * - getUserMedia() for microphone
 * - MediaRecorder for recording
 * - WebSocket for real-time transcription
 *
 * Can capture ANY application running on the system (Zoom, Teams, etc.)
 * User selects what to share via browser's native picker
 */


/**
 * Sections stream in independently (research_section events), so this is a
 * content check rather than a null check — used to switch the panel from
 * "still researching" to partial results as soon as anything has arrived.
 */
const isResearchEmpty = (research) =>
  !research ||
  (!research.company_overview?.summary &&
    !(research.decision_makers?.length > 0) &&
    !(research.priorities?.length > 0) &&
    !(research.tech_landscape?.length > 0));

/**
 * A pitch object always comes back fully shaped (the backend fills every key),
 * so "did we actually get a pitch?" is a content question, not a null check.
 */
const isPitchEmpty = (pitch) =>
  !pitch ||
  (!pitch.headline &&
    !pitch.positioning_statement &&
    !(pitch.value_propositions?.length > 0) &&
    !(pitch.proof_points?.length > 0) &&
    !(pitch.pain_hypotheses?.length > 0));

/** Flatten a pitch into the plain text a rep would paste into notes or email. */
const pitchToPlainText = (pitch) => {
  if (!pitch) return "";
  const lines = [];
  const section = (title, body) => {
    if (!body || (Array.isArray(body) && body.length === 0)) return;
    lines.push(title.toUpperCase());
    (Array.isArray(body) ? body : [body]).forEach((l) => lines.push(l));
    lines.push("");
  };

  if (pitch.headline) lines.push(pitch.headline, "");
  if (pitch.positioning_statement) lines.push(pitch.positioning_statement, "");
  section(
    "Why now",
    (pitch.why_now || []).map((w) =>
      [w.trigger, w.so_what].filter(Boolean).join(" — ")
    )
  );
  section(
    "Likely pains",
    (pitch.pain_hypotheses || []).map((h) =>
      [h.pain, h.evidence].filter(Boolean).join(" — ")
    )
  );
  section(
    "Value propositions",
    (pitch.value_propositions || []).map((v) =>
      [
        v.title,
        v.prospect_need && `Their need: ${v.prospect_need}`,
        v.simform_capability && `Simform: ${v.simform_capability}`,
        v.outcome && `Outcome: ${v.outcome}`,
      ]
        .filter(Boolean)
        .join("\n  ")
    )
  );
  section(
    "Proof points",
    (pitch.proof_points || []).map((pp) =>
      [pp.title, pp.relevance, pp.metrics].filter(Boolean).join(" — ")
    )
  );
  section("Differentiators", pitch.differentiators || []);
  section("Discovery questions", pitch.discovery_questions || []);
  section(
    "Objection handling",
    (pitch.objection_handling || []).map(
      (o) => `"${o.objection}" → ${o.response || ""}`
    )
  );
  const model = pitch.engagement_model || {};
  section(
    "Engagement model",
    [model.recommended, model.rationale].filter(Boolean).join(" — ") || null
  );
  section("Next step", pitch.call_to_action || null);

  return lines.join("\n").trim();
};

const NewMeeting = () => {
  const navigate = useNavigate();

  const [isCapturing, setIsCapturing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [transcripts, setTranscripts] = useState([]); // Final transcripts
  const [liveTranscripts, setLiveTranscripts] = useState([]); // Interim/live transcripts
  const [suggestions, setSuggestions] = useState([]);
  const [followUpQuestions, setFollowUpQuestions] = useState([]); // Follow-up question suggestions
  const [lastQuestion, setLastQuestion] = useState("");
  const [questionTimestamp, setQuestionTimestamp] = useState(null);

  const [audioLevel, setAudioLevel] = useState(0);
  const [sessionStats, setSessionStats] = useState({
    transcripts: 0,
    duration: 0,
  });

  // Transcript panel collapsed by default; users opt in.
  const [showTranscript, setShowTranscript] = useState(false);

  // ===== Account research (Customer AI Insights) panel =====
  const [showResearch, setShowResearch] = useState(false);
  const [prospectName, setProspectName] = useState("");
  const [accountResearch, setAccountResearch] = useState(null);
  // idle | running | done | error
  const [researchStatus, setResearchStatus] = useState("idle");
  const [researchStep, setResearchStep] = useState("");
  const [researchProgress, setResearchProgress] = useState(0);
  // Insights sub-tab: "research" (public account brief) | "pitch" (Simform pitch)
  const [insightsTab, setInsightsTab] = useState("research");
  const [accountPitch, setAccountPitch] = useState(null);
  // idle | running | done | error
  const [pitchStatus, setPitchStatus] = useState("idle");
  const [pitchStep, setPitchStep] = useState("");
  const [pitchProgress, setPitchProgress] = useState(0);
  // Per-panel font sizes (px) for in-panel +/- controls.
  const [fontSizes, setFontSizes] = useState({
    followUp: 14,
    suggestions: 14,
    transcript: 14,
    engineering: 13,
    relevantCards: 13,
  });

  // Tech Capabilities panel — live highlights from WS + panel layout hook
  const [capabilityHighlights, setCapabilityHighlights] = useState([]);
  // Relevant Cards panel — KB case-study cards (WS type "relevant_card")
  const [relevantCards, setRelevantCards] = useState([]);
  const { order: panelOrder, visible: panelVisible, toggleVisible: togglePanelVisible, resetLayout: resetPanelLayout } = usePanelLayout();

  // Confirmation modal for End Meeting action (icon-only button).
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  // Clear Session feature (cold-call mode only)
  const [isColdCallActive, setIsColdCallActive] = useState(false);
  const [sessionNumber, setSessionNumber] = useState(1);
  const [clearingSession, setClearingSession] = useState(false);
  const [showClearSessionModal, setShowClearSessionModal] = useState(false);
  const [clearSessionProspectInput, setClearSessionProspectInput] = useState("");

  // Meeting ID state
  const [meetingId, setMeetingId] = useState(null);
  // Latest stopCapture, for listeners registered before meetingId state settles
  // (e.g. the screen-share "ended" handler) so they don't call a stale closure.
  const stopCaptureRef = useRef(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  // Shows while the host is generating the summary on their side (participants only).
  // Set when "meeting_ending" WS arrives; stays until navigation.
  const [participantWaitingOverlay, setParticipantWaitingOverlay] = useState(false);
  // Shows when the host disconnected abnormally. Participant must manually dismiss.
  const [hostDisconnectedOverlay, setHostDisconnectedOverlay] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Capture options
  const [captureAudio, setCaptureAudio] = useState(true);
  const [captureMicrophone, setCaptureMicrophone] = useState(true);

  // Speaker color management
  const [speakerColors, setSpeakerColors] = useState({});

  // Refs
  const videoRef = useRef(null);
  const recognizerRef = useRef(null); // Azure Speech Recognizer
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const combinedStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const websocketRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const suggestionsContainerRef = useRef(null);
  const sessionStartTimeRef = useRef(null);
  const statsIntervalRef = useRef(null);

  // Toast notification state for AI suggestions
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState("");

  // Participants (live roster of host + everyone listening in).
  const [participants, setParticipants] = useState([]);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  // Participant mode = this client is NOT the host. They're just listening to
  // a meeting that's already in progress (no screen capture, no Azure Speech).
  const [isParticipantMode, setIsParticipantMode] = useState(false);
  // Mirrored as a ref so WS callbacks (created once, capture stale closures)
  // can read the latest mode without restarting the socket.
  const isParticipantModeRef = useRef(false);
  useEffect(() => { isParticipantModeRef.current = isParticipantMode; }, [isParticipantMode]);
  const isCapturingRef = useRef(false);
  useEffect(() => { isCapturingRef.current = isCapturing; }, [isCapturing]);
  const meetingEndTimeoutRef = useRef(null);
  // Mirror transcripts so stopCapture always reads the latest list without
  // needing transcripts in its useCallback dep array (which would recreate it
  // on every transcript update — very frequent during a meeting).
  const transcriptsRef = useRef([]);
  useEffect(() => { transcriptsRef.current = transcripts; }, [transcripts]);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState(null);

  useEffect(() => {
    if (isCapturing && !isParticipantMode) {
      window.__meetingLeaveGuard = (path) => {
        setPendingNavPath(path);
        setShowLeaveModal(true);
      };
    } else {
      window.__meetingLeaveGuard = null;
    }
    return () => {
      window.__meetingLeaveGuard = null;
    };
  }, [isCapturing, isParticipantMode]);
  // Mirror isPendingApproval for the same stale-closure reasons (ws.onclose
  // captures state once at WS creation time).
  const isPendingApprovalRef = useRef(false);
  // Set when the host ends the meeting and we're transitioning a participant
  // to the summary page. Stops ws.onclose from resetting state mid-navigation.
  const meetingEndingRef = useRef(false);
  // "Meeting already active" confirmation dialog before joining as participant.
  const [pendingJoin, setPendingJoin] = useState(null); // { meetingId, hostUserId, participants }

  // Host-approval gate state.
  // Requester side: waiting for the host to accept our join request.
  const [isPendingApproval, setIsPendingApproval] = useState(false);
  useEffect(() => { isPendingApprovalRef.current = isPendingApproval; }, [isPendingApproval]);
  // "declined" message shown briefly after the host rejects or the request times out.
  const [declinedMessage, setDeclinedMessage] = useState(null);
  // Host side: incoming pending requests (list rendered in Participants modal + toast).
  const [pendingRequests, setPendingRequests] = useState([]); // [{connection_id, user_id, display_name, email, requested_at}]
  // Subset of pendingRequests rendered as transient bottom-right toasts.
  const [pendingToasts, setPendingToasts] = useState([]); // same shape
  // Auto-dismiss each toast 8 s after it appears. The matching modal row stays
  // in pendingRequests until the host decides or the request resolves.
  useEffect(() => {
    if (pendingToasts.length === 0) return undefined;
    const timers = pendingToasts.map((p) =>
      setTimeout(() => {
        setPendingToasts((prev) => prev.filter((x) => x.connection_id !== p.connection_id));
      }, 8000)
    );
    return () => timers.forEach(clearTimeout);
  }, [pendingToasts]);

  // ===== Manual trigger (admin-config debounced "Generate now" button) =====
  const [manualTriggerInFlight, setManualTriggerInFlight] = useState(false);
  const [manualCooldownMs, setManualCooldownMs] = useState(0);
  const [manualTriggerError, setManualTriggerError] = useState(null);
  const manualCooldownIntervalRef = useRef(null);
  // Polling interval used while waiting for a meeting to start (invite link,
  // room not yet active).
  const waitingForHostIntervalRef = useRef(null);

  // Share-link state (host-only): copying invite URL to clipboard.
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  // Tracks whether this page load was triggered by an invite link so we can
  // show "Joining…" instead of "Waiting for host…" in the pending screen.
  const [isInviteJoining, setIsInviteJoining] = useState(false);
  // True when the user arrived via invite link but the meeting hasn't started yet.
  const [isWaitingForHostToStart, setIsWaitingForHostToStart] = useState(false);
  // Guards against the invite-URL auto-join firing more than once (userId
  // resolves asynchronously and triggers a re-render).
  const inviteAutoJoinedRef = useRef(false);

  // Authenticated user identity (resolved from MSAL or SSO localStorage).
  const [userId, setUserId] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    const initUser = async () => {
      try {
        // MSAL path: account.name = display name, account.username = UPN/email
        const { success, account } = await webAuthService.getAccount();
        if (success && account) {
          const id = account.localAccountId || account.homeAccountId;
          setUserId(id);
          setUserDisplayName(account.name || null);
          setUserEmail(account.username || null);
          console.log("✅ MSAL identity:", { id, name: account.name, email: account.username });
          return;
        }

        // SSO path: profile JSON was persisted to localStorage by /auth/sso-validate
        try {
          const ssoRaw = localStorage.getItem("userProfileData");
          if (ssoRaw) {
            const ssoProfile = JSON.parse(ssoRaw);
            if (ssoProfile?.id) {
              setUserId(ssoProfile.id);
              setUserDisplayName(ssoProfile.name || null);
              setUserEmail(ssoProfile.email || null);
              console.log("✅ SSO identity:", ssoProfile);
              return;
            }
          }
        } catch (e) {
          console.warn("⚠️ Failed to read SSO profile from localStorage:", e);
        }

        // Last resort: just the user_id from getUserId().
        const fallbackId = await webAuthService.getUserId();
        if (fallbackId) setUserId(fallbackId);
        else console.warn("⚠️ No authenticated user found");
      } catch (error) {
        console.error("❌ Failed to resolve user identity:", error);
      }
    };

    initUser();
  }, []);

  // Auto-join when the page is opened via an invite link (?meeting_id=…&invite_token=…).
  // Waits until userId is resolved so the WS connect carries a valid user_id.
  useEffect(() => {
    if (!userId || inviteAutoJoinedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const inviteMeetingId = params.get("meeting_id");
    const inviteToken = params.get("invite_token");
    if (!inviteMeetingId || !inviteToken) return;

    inviteAutoJoinedRef.current = true;
    // Strip the params from the URL so a refresh doesn't re-trigger the join.
    window.history.replaceState({}, "", window.location.pathname);

    const apiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "http://localhost:8080";

    const attemptJoin = () => {
      fetch(`${apiBase}/meetings/${encodeURIComponent(inviteMeetingId)}/active`)
        .then((r) => r.json())
        .then((body) => {
          if (body?.is_active) {
            // Meeting is live — clear any polling loop and connect.
            if (waitingForHostIntervalRef.current) {
              clearInterval(waitingForHostIntervalRef.current);
              waitingForHostIntervalRef.current = null;
            }
            setIsWaitingForHostToStart(false);
            setMeetingId(inviteMeetingId);
            setDeclinedMessage(null);
            // Show "Joining meeting…" optimistically. The backend will immediately
            // send either participant_approved (auto-approve for returning users or
            // invite-token holders) or approval_pending (needs host decision).
            // approval_pending will switch the text to "Waiting for host to admit you…"
            setIsInviteJoining(true);
            setIsPendingApproval(true);
            connectWebSocket(inviteMeetingId, true, inviteToken);
          } else if (body?.has_ended) {
            // Host explicitly ended this meeting. Stop polling and inform the user.
            if (waitingForHostIntervalRef.current) {
              clearInterval(waitingForHostIntervalRef.current);
              waitingForHostIntervalRef.current = null;
            }
            setIsWaitingForHostToStart(false);
            setDeclinedMessage("This meeting has ended. The host can start a new meeting at any time.");
            inviteAutoJoinedRef.current = false;
          } else {
            // Meeting hasn't started yet. Show waiting state and keep polling.
            setMeetingId(inviteMeetingId);
            setDeclinedMessage(null);
            setIsWaitingForHostToStart(true);
            if (!waitingForHostIntervalRef.current) {
              waitingForHostIntervalRef.current = setInterval(attemptJoin, 5000);
            }
          }
        })
        .catch(() => {
          // Pre-flight failed — connect anyway and let the backend decide.
          if (waitingForHostIntervalRef.current) {
            clearInterval(waitingForHostIntervalRef.current);
            waitingForHostIntervalRef.current = null;
          }
          setIsWaitingForHostToStart(false);
          setMeetingId(inviteMeetingId);
          setDeclinedMessage(null);
          setIsInviteJoining(true);
          setIsPendingApproval(true);
          connectWebSocket(inviteMeetingId, true, inviteToken);
        });
    };

    attemptJoin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Generate an invite link for the current meeting and copy it to the clipboard.
  const copyShareLink = useCallback(async () => {
    if (!meetingId || isCopyingLink) return;
    setIsCopyingLink(true);
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "http://localhost:8080";
      const resp = await fetch(
        `${apiBase}/meetings/${encodeURIComponent(meetingId)}/invite-token`,
        { method: "POST" }
      );
      const data = await resp.json();
      if (data.invite_token) {
        const url = `${window.location.origin}${window.location.pathname}?meeting_id=${encodeURIComponent(meetingId)}&invite_token=${encodeURIComponent(data.invite_token)}`;
        await navigator.clipboard.writeText(url);
        setShareLinkCopied(true);
        setTimeout(() => setShareLinkCopied(false), 2500);
      }
    } catch (err) {
      console.error("Failed to generate share link:", err);
    } finally {
      setIsCopyingLink(false);
    }
  }, [meetingId, isCopyingLink]);

  // Auto-scroll transcript container
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop =
        transcriptContainerRef.current.scrollHeight;
    }
  }, [transcripts, liveTranscripts]);

  // Auto-scroll suggestions container to top when new suggestions arrive
  useEffect(() => {
    if (suggestionsContainerRef.current && suggestions.length > 0) {
      suggestionsContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [suggestions.length]);

  // ===== Manual trigger cooldown countdown =====
  useEffect(() => {
    if (manualCooldownMs <= 0) {
      if (manualCooldownIntervalRef.current) {
        clearInterval(manualCooldownIntervalRef.current);
        manualCooldownIntervalRef.current = null;
      }
      return;
    }
    if (manualCooldownIntervalRef.current) return;
    manualCooldownIntervalRef.current = setInterval(() => {
      setManualCooldownMs((prev) => Math.max(0, prev - 200));
    }, 200);
    return () => {
      if (manualCooldownIntervalRef.current) {
        clearInterval(manualCooldownIntervalRef.current);
        manualCooldownIntervalRef.current = null;
      }
    };
  }, [manualCooldownMs]);

  // Send a manual_trigger request over the WebSocket
  const triggerManualSuggestion = useCallback(() => {
    if (manualTriggerInFlight || manualCooldownMs > 0) return;
    const ws = websocketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setManualTriggerError("WebSocket is not connected.");
      setTimeout(() => setManualTriggerError(null), 3000);
      return;
    }
    setManualTriggerInFlight(true);
    setManualTriggerError(null);
    try {
      ws.send(JSON.stringify({ type: "manual_trigger" }));
    } catch (err) {
      console.error("manual_trigger send error", err);
      setManualTriggerInFlight(false);
      setManualTriggerError("Failed to send trigger request.");
      setTimeout(() => setManualTriggerError(null), 3000);
    }
  }, [manualTriggerInFlight, manualCooldownMs]);

  /**
   * Generate a random color for a speaker
   * Uses HSL color space for consistent brightness and saturation
   */
  const generateSpeakerColor = useCallback(
    (speakerId) => {
      // Check if we already have a color for this speaker
      if (speakerColors[speakerId]) {
        return speakerColors[speakerId];
      }

      // Generate a random hue (0-360) for color variety
      const hue = Math.floor(Math.random() * 360);

      // Use consistent saturation and lightness for light theme compatibility
      const saturation = 65 + Math.floor(Math.random() * 20); // 65-85%
      const lightness = 30 + Math.floor(Math.random() * 15); // 30-45% (darker for light theme)

      const color = {
        // Background color (light, semi-transparent)
        bg: `hsla(${hue}, ${saturation}%, ${lightness + 50}%, 0.1)`,
        // Border color (medium)
        border: `hsl(${hue}, ${saturation}%, ${lightness + 30}%)`,
        // Text/icon color (dark for readability)
        text: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        // Badge background (light)
        badge: `hsla(${hue}, ${saturation}%, ${lightness + 50}%, 0.3)`,
      };

      // Store the color for this speaker
      setSpeakerColors((prev) => ({
        ...prev,
        [speakerId]: color,
      }));

      return color;
    },
    [speakerColors]
  );

  /**
   * Get color for a speaker (generates if not exists)
   */
  const getSpeakerColor = useCallback(
    (speakerId) => {
      return speakerColors[speakerId] || generateSpeakerColor(speakerId);
    },
    [speakerColors, generateSpeakerColor]
  );

  /**
   * Start screen and audio capture using Web APIs
   */
  const startCapture = useCallback(async () => {
    try {
      // Show meeting ID modal first
      setShowMeetingModal(true);
    } catch (error) {
      console.error("❌ Failed to start capture:", error);
    }
  }, []);

  /**
   * Fetch Azure Speech SDK configuration from backend
   */
  const fetchSpeechConfig = useCallback(async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/api/speech-config`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch speech config");
      }

      const data = await response.json();
      console.log("📡 Speech config received:", data);

      // Handle backend response format: { body: { token, region } }
      if (data.body) {
        return data.body;
      }

      // Fallback for direct format: { token, region }
      return data;
    } catch (error) {
      console.error("❌ Failed to fetch speech config:", error);
      throw error;
    }
  }, []);

  /**
   * Handle Azure Speech transcript (interim and final)
   */
  const handleAzureTranscript = useCallback((text, speakerId, isFinal) => {
    if (!text || !text.trim()) return;

    const transcript = {
      id: `${Date.now()}-${speakerId}`,
      text: text,
      timestamp: Date.now(),
      confidence: 0.9,
      isFinal: isFinal,
      speaker: speakerId,
      type: "transcript",
    };

    if (!isFinal) {
      // Handle interim (live) transcripts
      setLiveTranscripts((prev) => {
        const existingIndex = prev.findIndex((t) => t.speaker === speakerId);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = transcript;
          return updated;
        }
        return [...prev, transcript];
      });
    } else {
      // Handle final transcripts - clear all live transcripts when any speaker finishes
      setLiveTranscripts([]);

      // Add to final transcripts
      setTranscripts((prev) => [...prev, transcript]);

      setSessionStats((stats) => ({
        ...stats,
        transcripts: stats.transcripts + 1,
      }));
    }
  }, []);

  /**
   * Handle meeting ID confirmation and start actual capture with Azure Speech SDK.
   * Before doing anything destructive (screen share prompt), pre-flight against the
   * backend: if the meeting_id is already live we route to participant mode.
   */
  const handleMeetingIdConfirm = useCallback(async (confirmedMeetingId, groupId = null, selectedKBFiles = [], confirmedProspectName = "", isColdCall = false, selectedExtCollections = []) => {
    try {
      // Ensure userId is available
      if (!userId) {
        console.error("❌ Cannot start meeting: User not authenticated");
        alert("Please wait for authentication to complete and try again.");
        setShowMeetingModal(false);
        return;
      }

      // Persist KB/group selections up-front so the participant WS connect can pick them up too.
      if (groupId) {
        window.sessionStorage.setItem(`meeting_${confirmedMeetingId}_group`, groupId);
      }
      if (selectedKBFiles && selectedKBFiles.length > 0) {
        window.sessionStorage.setItem(
          `meeting_${confirmedMeetingId}_kb_files`,
          JSON.stringify(selectedKBFiles)
        );
      }
      if (selectedExtCollections && selectedExtCollections.length > 0) {
        window.sessionStorage.setItem(
          `meeting_${confirmedMeetingId}_ext_collections`,
          JSON.stringify(selectedExtCollections)
        );
      }

      // Pre-flight: is this meeting already in progress?
      try {
        const apiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "http://localhost:8080";
        const resp = await fetch(`${apiBase}/meetings/${encodeURIComponent(confirmedMeetingId)}/active`);
        if (resp.ok) {
          const body = await resp.json();
          if (body && body.is_active) {
            if (body.host_user_id && body.host_user_id === userId) {
              // We ARE the host of this meeting (or the known host reconnecting
              // after a tab refresh). Fall through to the normal start path —
              // the backend will recognize us and update the primary WS slot.
            } else {
              // Another user is hosting — surface the "already active" prompt.
              setPendingJoin({
                meetingId: confirmedMeetingId,
                hostUserId: body.host_user_id || null,
                participants: body.participants || [],
              });
              setShowMeetingModal(false);
              return;
            }
          }
        }
      } catch (preflightErr) {
        // Pre-flight is best-effort. If it fails, fall through to the normal start path —
        // the backend will still prevent a duplicate ingest at WS connect time.
        console.warn("Active-meeting pre-flight failed; continuing with host flow:", preflightErr);
      }

      setMeetingId(confirmedMeetingId);
      setShowMeetingModal(false);
      setIsInitializing(true);

      // Store group_id for WebSocket connection
      if (groupId) {
        window.sessionStorage.setItem(
          `meeting_${confirmedMeetingId}_group`,
          groupId
        );
      }

      // Store prospect name + reset research state for the Account Insights panel
      const trimmedProspect = (confirmedProspectName || "").trim();
      setProspectName(trimmedProspect);
      setAccountResearch(null);
      setResearchStep("");
      setResearchProgress(0);
      setAccountPitch(null);
      setPitchStep("");
      setPitchProgress(0);
      setInsightsTab("research");
      if (trimmedProspect) {
        window.sessionStorage.setItem(
          `meeting_${confirmedMeetingId}_prospect`,
          trimmedProspect
        );
        setResearchStatus("running");
        setPitchStatus("running"); // the pitch follows the research in the same task
        setShowResearch(true); // auto-open the panel when research will run
      } else {
        window.sessionStorage.removeItem(`meeting_${confirmedMeetingId}_prospect`);
        setResearchStatus("idle");
        setPitchStatus("idle");
      }

      // Store selected KB file IDs for WebSocket connection
      if (selectedKBFiles && selectedKBFiles.length > 0) {
        window.sessionStorage.setItem(
          `meeting_${confirmedMeetingId}_kb_files`,
          JSON.stringify(selectedKBFiles)
        );
        console.log(`📚 KB files selected for meeting: ${selectedKBFiles.length} file(s)`, selectedKBFiles);
      } else {
        window.sessionStorage.removeItem(`meeting_${confirmedMeetingId}_kb_files`);
        console.log("📚 No KB files selected for this meeting");
      }

      // Store selected external collection IDs for WebSocket connection
      if (selectedExtCollections && selectedExtCollections.length > 0) {
        window.sessionStorage.setItem(
          `meeting_${confirmedMeetingId}_ext_collections`,
          JSON.stringify(selectedExtCollections)
        );
        console.log(`🗄️ External collections selected for meeting: ${selectedExtCollections.length}`, selectedExtCollections);
      } else {
        window.sessionStorage.removeItem(`meeting_${confirmedMeetingId}_ext_collections`);
      }

      // Store cold call flag for WebSocket connection
      if (isColdCall) {
        window.sessionStorage.setItem(`meeting_${confirmedMeetingId}_cold_call`, "true");
        console.log("📞 Cold call mode enabled for meeting:", confirmedMeetingId);
      } else {
        window.sessionStorage.removeItem(`meeting_${confirmedMeetingId}_cold_call`);
      }
      setIsColdCallActive(!!isColdCall);
      setSessionNumber(1);

      // Reset speaker colors for new meeting
      setSpeakerColors({});

      // Fetch Azure Speech config
      const speechConfig = await fetchSpeechConfig();

      // Check if Azure Speech SDK is loaded
      if (typeof SpeechSDK === "undefined") {
        throw new Error(
          "Speech recognition service not loaded. Please refresh the page."
        );
      }

      // Validate speech config
      if (!speechConfig || !speechConfig.token || !speechConfig.region) {
        throw new Error(
          "Speech recognition service is not properly configured. Please contact support."
        );
      }

      // Create Speech SDK configuration
      const azureSpeechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        speechConfig.token,
        speechConfig.region
      );
      azureSpeechConfig.speechRecognitionLanguage = "en-US";

      // 1. Request microphone
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
      micStreamRef.current = micStream;

      // 2. Request display media (screen/tab with audio)
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: captureAudio
          ? {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
              sampleRate: 16000,
              channelCount: 1,
            }
          : false,
      });
      displayStreamRef.current = displayStream;
      console.log("✅ Display capture granted");

      // 3. Mix audio streams
      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;
      const destination = audioContext.createMediaStreamDestination();

      // Connect microphone
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);

      // Connect display audio if available
      const displayAudioTracks = displayStream.getAudioTracks();
      if (displayAudioTracks.length > 0) {
        const displaySource =
          audioContext.createMediaStreamSource(displayStream);
        displaySource.connect(destination);
        console.log("✅ Mixed microphone + display audio");
      } else {
        console.log("✅ Using microphone only");
      }

      // 4. Create combined stream for preview
      const videoTracks = displayStream.getVideoTracks();
      const mixedAudioTracks = destination.stream.getAudioTracks();
      const combinedStream = new MediaStream([
        ...videoTracks,
        ...mixedAudioTracks,
      ]);
      combinedStreamRef.current = combinedStream;

      // 5. Setup video preview
      if (videoRef.current) {
        videoRef.current.srcObject = combinedStream;
        videoRef.current.play();
      }

      // 6. Setup Azure Speech Recognition
      const mixedStream = destination.stream;
      const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(mixedStream);
      const recognizer = new SpeechSDK.ConversationTranscriber(
        azureSpeechConfig,
        audioConfig
      );
      recognizerRef.current = recognizer;

      // Connect WebSocket for backend communication
      connectWebSocket(confirmedMeetingId);

      // 7. Setup recognizer event handlers
      recognizer.sessionStarted = () => {
        console.log("✅ Azure Speech session started");
        setConnectionStatus("connected");
      };

      recognizer.sessionStopped = () => {
        console.log("⏹️ Azure Speech session stopped");
        setConnectionStatus("disconnected");
      };

      // Handle interim transcriptions
      recognizer.transcribing = (_s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
          const speakerId = e.result.speakerId || "Unknown Speaker";
          handleAzureTranscript(e.result.text, speakerId, false);
        }
      };

      // Handle final transcriptions
      recognizer.transcribed = (_s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const speakerId = e.result.speakerId || "Unknown Speaker";
          handleAzureTranscript(e.result.text, speakerId, true);

          // Send to backend via WebSocket
          if (websocketRef.current?.readyState === WebSocket.OPEN) {
            websocketRef.current.send(
              JSON.stringify({
                type: "transcription",
                speakerId: speakerId,
                text: e.result.text,
                timestamp: new Date().toISOString(),
                isFinal: true,
              })
            );
          }
        }
      };

      recognizer.canceled = (_s, e) => {
        console.error("❌ Azure Speech canceled:", e.reason);
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          console.error("Error details:", e.errorDetails);
        }
      };

      // 8. Start the recognizer
      recognizer.startTranscribingAsync(
        () => {
          console.log("✅ Azure Speech recognition started");
          setIsCapturing(true);
          setIsInitializing(false);
          sessionStartTimeRef.current = Date.now();
          startSessionTimer();
        },
        (error) => {
          console.error("❌ Failed to start recognition:", error);
          setIsInitializing(false);
        }
      );

      // 9. Handle the screen-share video track ending. The browser/OS can end it on
      // its own (display change, sleep, permission), not only on "Stop sharing".
      // Transcription only needs audio, so keep the meeting running on the mic.
      displayStream.getVideoTracks()[0].addEventListener("ended", () => {
        const micLive = micStreamRef.current
          ?.getAudioTracks()
          .some((t) => t.readyState === "live");
        if (micLive) {
          console.warn("📹 Screen share ended — continuing on microphone audio");
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          return;
        }
        console.log("📹 Screen share and microphone ended — stopping capture");
        stopCaptureRef.current?.();
      });
    } catch (error) {
      console.error("❌ Capture failed:", error);

      let errorMessage = "Failed to start capture";
      if (error.name === "NotAllowedError") {
        errorMessage =
          "Permission denied. Please allow screen sharing and microphone access.";
      } else if (error.name === "NotFoundError") {
        errorMessage = "No display or microphone found.";
      } else if (error.name === "NotReadableError") {
        errorMessage = "Screen or microphone is already in use.";
      } else {
        errorMessage = error.message || "Unknown error occurred";
      }

      setShowMeetingModal(false);
      setIsInitializing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureAudio, fetchSpeechConfig, handleAzureTranscript, userId]);

  /**
   * Setup audio visualization
   */
  const setupAudioVisualization = useCallback((stream) => {
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn("⚠️ No audio tracks for visualization");
      return;
    }

    try {
      // Don't create a new context if we already have one
      const audioContext = audioContextRef.current || new AudioContext();
      if (!audioContextRef.current) {
        audioContextRef.current = audioContext;
      }

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(
        new MediaStream(audioTracks)
      );
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        if (!isCapturing) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const level = Math.min(100, (average / 255) * 150); // Amplify for better visibility
        setAudioLevel(level);

        requestAnimationFrame(updateLevel);
      };

      updateLevel();
      analyserRef.current = analyser;

      console.log("✅ Audio visualization setup complete");
    } catch (error) {
      console.error("❌ Failed to setup audio visualization:", error);
    }
  }, [isCapturing]);

  /**
   * Connect to backend WebSocket for real-time transcription.
   * When asParticipant=true, joins an existing meeting as a passive listener
   * (no audio ingest — backend handles this based on handler_type=participant).
   */
  const connectWebSocket = useCallback(async (meetingId, asParticipant = false, inviteToken = null) => {
    try {
      // Make sure we have a userId
      if (!userId) {
        console.error("❌ Cannot connect WebSocket: userId is null");
        return;
      }

      // Get WebSocket URL from environment or default
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsHost = import.meta.env.VITE_WS_HOST || window.location.hostname;
      const wsPort = import.meta.env.VITE_WS_PORT || "8080";

      // Get group_id from session storage if it was set
      const groupId = window.sessionStorage.getItem(
        `meeting_${meetingId}_group`
      );

      // Get selected KB file IDs from session storage if set
      const kbFilesRaw = window.sessionStorage.getItem(
        `meeting_${meetingId}_kb_files`
      );
      const kbFileIds = kbFilesRaw ? JSON.parse(kbFilesRaw) : [];

      // Get selected external collection IDs from session storage
      const extCollRaw = window.sessionStorage.getItem(
        `meeting_${meetingId}_ext_collections`
      );
      const extCollIds = extCollRaw ? JSON.parse(extCollRaw) : [];

      // Get prospect name for account research from session storage
      const prospect = window.sessionStorage.getItem(
        `meeting_${meetingId}_prospect`
      );

      // Host: handler_type=text (Azure Speech SDK runs locally and posts transcripts).
      // Participant: handler_type=participant (passive — receives broadcasts only,
      // no screen share, no Azure Speech). The participant flow joins an
      // already-active meeting without disturbing the existing ingest.
      const handlerType = asParticipant ? "participant" : "text";
      let wsUrl = `${wsProtocol}//${wsHost}:${wsPort}/ws?meeting_id=${meetingId}&user_id=${userId}&handler_type=${handlerType}`;
      if (groupId) {
        wsUrl += `&group_id=${groupId}`;
      }
      if (kbFileIds.length > 0) {
        wsUrl += `&kb_file_ids=${encodeURIComponent(JSON.stringify(kbFileIds))}`;
      }
      if (extCollIds.length > 0) {
        wsUrl += `&kb_collection_ids=${encodeURIComponent(JSON.stringify(extCollIds))}`;
      }
      if (prospect) {
        wsUrl += `&prospect_name=${encodeURIComponent(prospect)}`;
      }
      // Pass the user's display name + email through to the backend so the
      // participants modal can show real names without depending on a Graph
      // client-credentials token (AZURE_CLIENT_SECRET) on the server.
      if (userDisplayName) {
        wsUrl += `&display_name=${encodeURIComponent(userDisplayName)}`;
      }
      if (userEmail) {
        wsUrl += `&email=${encodeURIComponent(userEmail)}`;
      }
      if (inviteToken) {
        wsUrl += `&invite_token=${encodeURIComponent(inviteToken)}`;
      }
      const coldCallRaw = window.sessionStorage.getItem(`meeting_${meetingId}_cold_call`);
      if (coldCallRaw === "true") {
        wsUrl += "&cold_call=true";
      }

      console.log("🔌 Connecting to WebSocket:", wsUrl);
      console.log(
        "📋 Meeting ID:",
        meetingId,
        "User ID:",
        userId,
        "Group ID:",
        groupId || "None",
        "KB Files:",
        kbFileIds.length > 0 ? kbFileIds : "None",
        "Ext Collections:",
        extCollIds.length > 0 ? extCollIds : "None",
        "Handler: text (Azure Speech SDK)"
      );

      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log("✅ WebSocket connected");
        setConnectionStatus("connected");
        // Tell the auth layer a call is live so it won't tear the session
        // down underneath us (see services/meetingSession.js).
        setMeetingActive(true);

        // Audio visualization is only meaningful for the host (we own the mic stream).
        // Participants have no local stream to visualize.
        if (!asParticipant && combinedStreamRef.current) {
          setupAudioVisualization(combinedStreamRef.current);
        }

        // Rehydrate any previously persisted account research (e.g. after a
        // refresh/reconnect). Fresh research from the WS will overwrite this.
        if (prospect) {
          const baseURL =
            import.meta.env.VITE_API_URL || "http://localhost:8080";
          fetch(`${baseURL}/meeting_research/${encodeURIComponent(meetingId)}`)
            .then((r) => r.json())
            .then((res) => {
              if (res?.status === "success" && res.research) {
                setAccountResearch((prev) => prev || res.research);
                setResearchStatus((prev) => (prev === "done" ? prev : "done"));
              }
              if (res?.status === "success" && res.pitch) {
                setAccountPitch((prev) => prev || res.pitch);
                setPitchStatus((prev) => (prev === "done" ? prev : "done"));
              }
            })
            .catch((err) =>
              console.warn("[RESEARCH] rehydrate fetch failed:", err)
            );
        }
      };

      ws.onmessage = (event) => {
        try {
          // Skip empty messages
          if (!event.data || event.data === "") {
            return;
          }

          const data = JSON.parse(event.data);
          console.log("📩 WebSocket message:", data);

          // Handle different message types from backend
          if (data.type === "audio_transcript") {
            // Backend format: { type: "audio_transcript", transcript: "...", speaker_id: "...", timestamp: ..., meeting_id: "..." }
            handleTranscript({
              text: data.transcript,
              speaker: data.speaker_id || "Unknown",
              timestamp: data.timestamp,
            });
          } else if (data.type === "approval_pending") {
            // Backend confirmed it received our join request. Ensure the approval
            // spinner is visible — this is authoritative even if sessionStorage had
            // a stale "already approved" flag from a prior instance.
            console.log("⏳ Awaiting host approval for meeting:", data.meeting_id);
            setIsPendingApproval(true);
            setIsInviteJoining(false);
          } else if (data.type === "participant_approved") {
            // Host accepted us (or invite token bypassed the gate).
            // Promote into the full meeting UI; the server will follow up
            // with joined_existing_meeting + replay events.
            console.log("✅ Host approved join request");
            setIsPendingApproval(false);
            setIsInviteJoining(false);
            setIsParticipantMode(true);
            setIsCapturing(true);
            sessionStartTimeRef.current = Date.now();
            startSessionTimer();
          } else if (data.type === "participant_declined") {
            // Host declined or the 60s timer ran out. Show a brief message and
            // tear down the WS / state so the user returns to the start screen.
            const reasonText =
              data.reason === "timeout" ? "Request timed out — the host didn't respond."
              : data.reason === "host_left" ? "The host left the meeting before responding."
              : data.reason === "meeting_ended" ? "The meeting has ended."
              : "The host declined your join request.";
            console.log("❌ Join declined:", data.reason);
            setDeclinedMessage(reasonText);
            cancelPendingJoin();
          } else if (data.type === "participant_request") {
            // Host receives a new join request — show toast + add to pending list.
            const req = {
              connection_id: data.connection_id,
              user_id: data.user_id,
              display_name: data.display_name,
              email: data.email,
              requested_at: data.requested_at,
            };
            setPendingRequests((prev) => {
              if (prev.some((p) => p.connection_id === req.connection_id)) return prev;
              return [req, ...prev];
            });
            setPendingToasts((prev) => {
              if (prev.some((p) => p.connection_id === req.connection_id)) return prev;
              return [req, ...prev];
            });
          } else if (data.type === "participant_request_resolved") {
            // Host's side: cleanup pending entry once decided (or canceled/timed out).
            setPendingRequests((prev) => prev.filter((p) => p.connection_id !== data.connection_id));
            setPendingToasts((prev) => prev.filter((p) => p.connection_id !== data.connection_id));
          } else if (data.type === "pending_requests_update") {
            // Authoritative pending list from the backend — replace local state.
            // This is the resilient fallback for `participant_request_resolved`:
            // even if that point-to-point message was lost, the next mutation
            // re-syncs us. Fires after every pending mutation (join request,
            // approve, decline, timeout, cancel).
            const newList = Array.isArray(data.pending) ? data.pending : [];
            setPendingRequests(newList);
            // Drop any toasts for entries that aren't pending anymore.
            setPendingToasts((prev) =>
              prev.filter((t) =>
                newList.some((p) => p.connection_id === t.connection_id)
              )
            );
          } else if (data.type === "participant_join") {
            console.log("👋 Participant joined:", data.user_id);
          } else if (data.type === "participant_leave") {
            console.log("👋 Participant left:", data.user_id, "ws=", data.connection_id);
            // Defensive removal by connection_id. The follow-up participants_update
            // is authoritative, but if it's delayed or dropped we don't want the
            // departing user lingering in the list as "Unknown user".
            if (data.connection_id) {
              setParticipants((prev) =>
                prev.filter((p) => p.connection_id !== data.connection_id)
              );
            }
          } else if (data.type === "participants_update") {
            // Authoritative roster — replaces any local tracking.
            setParticipants(Array.isArray(data.participants) ? data.participants : []);
          } else if (data.type === "joined_existing_meeting") {
            // Sent only to the joiner. Hydrate the initial roster.
            setParticipants(Array.isArray(data.participants) ? data.participants : []);
          } else if (data.type === "room_join") {
            console.log("👋 User joined:", data.user_id);
          } else if (data.type === "suggestion_response") {
            // Backend sends: { type: "suggestion_response", needs_help: true/false, suggestion: "...", last_question: "..." }
            console.log("💡 Suggestion received:", data);

            if (data.suggestion) {
              handleSuggestion({
                id: data.id,
                text: data.suggestion,
                needs_help: data.needs_help,
                last_question: data.last_question,
                context_window: data.context_window,
                trigger_kind: data.trigger_kind,
                timestamp: Date.now(),
              });
            }

            // Update last question if provided
            if (data.last_question) {
              setLastQuestion(data.last_question);
            }

            // Whatever triggered this (auto or manual), release the manual flag
            setManualTriggerInFlight(false);
          } else if (data.type === "suggestion") {
            // Legacy format (if backend sends this)
            handleSuggestion(data);
          } else if (data.type === "followup_question") {
            handleFollowUpQuestion(data);
          } else if (data.type === "meeting_ending") {
            // Host started the end-meeting flow on the backend; LLM is now generating
            // the summary. Show an overlay so participants know the meeting is closing.
            if (isParticipantModeRef.current) {
              setParticipantWaitingOverlay(true);
            }
          } else if (data.type === "host_disconnected") {
            // Host's connection dropped abnormally (crash, network loss). The backend
            // waited 15 s to confirm it wasn't a normal end-meeting flow, then sent this.
            if (isParticipantModeRef.current) {
              setParticipantWaitingOverlay(false);
              setIsGeneratingSummary(false);
              setHostDisconnectedOverlay(true);
              setIsCapturing(false);
              setIsParticipantMode(false);
              setParticipants([]);
            }
          } else if (data.type === "meeting_end") {
            // Host (or server) ended the meeting. Participants get routed to the
            // summary page (the host already navigates from stopCapture). We
            // don't gate this on isParticipantModeRef — onclose can race ahead
            // and wipe participant state before this handler fires. If we have
            // a meeting_id, navigate; that's the only signal we need.
            console.log("🏁 Meeting end received:", data.reason);
            const targetMeetingId = data.meeting_id || meetingId;
            if (targetMeetingId) {
              // (no sessionStorage approval flags to clear — backend drives state)
              meetingEndingRef.current = true;
              setIsCapturing(false);
              setIsGeneratingSummary(true);
              const summaryData = data.summary !== undefined
                ? {
                    status: "success",
                    summary: data.summary,
                    notes_content: data.notes_content ?? data.summary,
                    action_items: data.action_items ?? [],
                  }
                : null;
              // Cancel any fallback timeout started by onclose (race condition)
              if (meetingEndTimeoutRef.current) {
                clearTimeout(meetingEndTimeoutRef.current);
              }
              meetingEndTimeoutRef.current = setTimeout(() => {
                meetingEndTimeoutRef.current = null;
                setIsGeneratingSummary(false);
                if (summaryData) {
                  navigate(`/meeting-summary/${targetMeetingId}`, {
                    state: { summaryData },
                  });
                } else {
                  navigate(`/meeting-summary/${targetMeetingId}`);
                }
              }, 1500);
            }
          } else if (data.type === "research_progress") {
            setResearchStatus("running");
            setResearchStep(data.step || "");
            setResearchProgress(
              typeof data.progress === "number" ? data.progress : 0
            );
          } else if (data.type === "research_complete") {
            setAccountResearch(data.research || null);
            setResearchStatus("done");
            setResearchProgress(1);
            setResearchStep("");
          } else if (data.type === "research_section") {
            // Arrives mid-run, before research_complete — merge it in so the
            // panel reveals each part as soon as it's ready instead of
            // waiting for the whole research object.
            setAccountResearch((prev) => ({
              ...(prev || {}),
              [data.field]: data.value,
            }));
          } else if (data.type === "capability_highlight") {
            setCapabilityHighlights((prev) => {
              if (prev.some((c) => c.id === data.id)) return prev;
              return [...prev, data];
            });
          } else if (data.type === "relevant_card") {
            // KB case-study cards → dedicated Relevant Cards panel (not Engineering Practices)
            setRelevantCards((prev) => {
              if (data.id && prev.some((c) => c.id === data.id)) return prev;
              return [...prev, data];
            });
          } else if (data.type === "pitch_progress") {
            setPitchStatus("running");
            setPitchStep(data.step || "");
            setPitchProgress(
              typeof data.progress === "number" ? data.progress : 0
            );
          } else if (data.type === "pitch_complete") {
            setAccountPitch(data.pitch || null);
            setPitchStatus("done");
            setPitchProgress(1);
            setPitchStep("");
          } else if (data.type === "pitch_section") {
            // Each group (narrative / value / proof / discovery) resolves on
            // its own timeline — merge its fields in as they land rather
            // than waiting for pitch_complete to reveal anything.
            setAccountPitch((prev) => ({
              ...(prev || {}),
              ...(data.value || {}),
            }));
          } else if (data.type === "pitch_error") {
            setPitchStatus("error");
            setPitchStep(data.message || "Pitch generation failed.");
          } else if (data.type === "research_error") {
            setResearchStatus("error");
            setResearchStep(data.message || "Account research failed.");
            // The pitch runs off the research, so it can never arrive now.
            setPitchStatus("error");
            setPitchStep("Pitch needs account research to succeed first.");
          } else if (data.type === "manual_trigger_accepted") {
            // Backend will produce a suggestion + followups asynchronously.
            // Keep in-flight true; the regular suggestion_response / followup_question
            // events will arrive and the in-flight flag is cleared in a timeout below.
            console.log("⚡ Manual trigger accepted");
            setManualTriggerError(null);
            // Safety auto-release after 20s so the UI never gets stuck if the
            // backend never responds with a suggestion (e.g. empty window).
            setTimeout(() => setManualTriggerInFlight(false), 20000);
          } else if (data.type === "manual_trigger_rejected") {
            console.log("⚡ Manual trigger rejected:", data);
            setManualTriggerInFlight(false);
            if (data.reason === "cooldown" && data.retry_after_ms) {
              setManualCooldownMs(data.retry_after_ms);
              setManualTriggerError(null);
            } else if (data.reason === "in_flight") {
              setManualTriggerError("A suggestion is already being generated.");
              setTimeout(() => setManualTriggerError(null), 3000);
            }
          } else if (data.type === "company_reset") {
            // Broadcast on every clear_session (manual or extension-driven) and
            // on the extension's /prospect push. Applied uniformly for every
            // client, including the host who initiated it — the previous
            // call's company info must never carry over into the next session.
            setProspectName(data.prospect_name || "");
            setAccountResearch(null);
            setResearchStatus(data.research_status === "pending" ? "running" : "idle");
            setResearchProgress(0);
            setResearchStep("");
            setAccountPitch(null);
            setPitchStatus(data.research_status === "pending" ? "running" : "idle");
            setPitchProgress(0);
            setPitchStep("");
            setInsightsTab("research");
          } else if (data.type === "session_cleared") {
            // Host broadcast this after clearing; participants apply the same reset.
            // The host's own clearSession() click already wiped local state before
            // this broadcast lands, so skip re-applying it — but ONLY for that path.
            // An extension-driven clear authenticates as the same user_id (cleared_by
            // can't tell the two apart), yet never touched local state, so the host
            // must still apply it even though cleared_by === userId in that case too.
            if (data.via === "extension" || data.cleared_by !== userId) {
              setSessionNumber(data.session_number ?? 1);
              setTranscripts([]);
              setLiveTranscripts([]);
              setSuggestions([]);
              setFollowUpQuestions([]);
              setCapabilityHighlights([]);
              setLastQuestion("");
              transcriptsRef.current = [];
              sessionStartTimeRef.current = Date.now();
            }
          } else if (data.type === "error") {
            console.error("❌ WebSocket error:", data.message);
          }
        } catch (error) {
          // Only log if it's not an empty string
          if (event.data && event.data !== "") {
            console.warn(
              "⚠️ Could not parse WebSocket message:",
              event.data,
              error
            );
          }
        }
      };

      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        setConnectionStatus("error");
      };

      ws.onclose = () => {
        console.log("🔌 WebSocket disconnected");
        setConnectionStatus("disconnected");
        // Call is over (or dropped) — a deferred logout may now proceed.
        setMeetingActive(false);
        // If we were a passive participant, the socket closing means the host ended
        // the meeting (or our connection dropped). Reset the UI to the start screen
        // since participants have nothing to clean up server-side.
        // Skip the reset when the meeting_end handler is mid-flight transitioning
        // to the summary page — clobbering isParticipantMode/isCapturing here would
        // briefly unmount the loading overlay before navigate() fires.
        if (isParticipantModeRef.current && !meetingEndingRef.current) {
          const wasCapturing = isCapturingRef.current;
          stopSessionTimer();
          setIsCapturing(false);
          setIsParticipantMode(false);
          setParticipants([]);
          websocketRef.current = null;
          if (wasCapturing) {
            // WS closed before meeting_end arrived — show overlay as fallback.
            // If meeting_end arrives within 2s it will cancel this timeout and
            // navigate with the actual summary data instead.
            meetingEndingRef.current = true;
            setIsGeneratingSummary(true);
            meetingEndTimeoutRef.current = setTimeout(() => {
              meetingEndTimeoutRef.current = null;
              setIsGeneratingSummary(false);
              navigate(`/meeting-summary/${meetingId}`);
            }, 2000);
          }
        }
        // If we were waiting for host approval and the socket dropped without an
        // explicit participant_declined first (network blip, host quit before
        // deciding, etc.), surface a generic "connection lost" message and return
        // to the start screen.
        if (isPendingApprovalRef.current) {
          setIsPendingApproval(false);
          setDeclinedMessage((prev) =>
            prev || "Connection to the host was lost before your request was answered."
          );
          websocketRef.current = null;
        }
      };
    } catch (error) {
      console.error("❌ Failed to connect WebSocket:", error);
    }
  }, [userId, userDisplayName, userEmail, lastQuestion, setupAudioVisualization]);

  /**
   * Handle incoming transcript
   */
  // Normalize various timestamp formats (seconds, milliseconds, ISO string)
  const toTimestampMs = useCallback((ts) => {
    if (!ts && ts !== 0) return null;
    // If it's already a number
    if (typeof ts === "number") {
      // If value looks like seconds (e.g. 1.7e9) convert to ms
      return ts < 1e12 ? Math.round(ts * 1000) : Math.round(ts);
    }

    // If it's a numeric string
    if (typeof ts === "string") {
      const trimmed = ts.trim();
      // ISO string -> Date.parse
      const isoParse = Date.parse(trimmed);
      if (!Number.isNaN(isoParse)) {
        return isoParse;
      }

      // numeric string
      const asNum = Number(trimmed);
      if (!Number.isNaN(asNum)) {
        return asNum < 1e12 ? Math.round(asNum * 1000) : Math.round(asNum);
      }
    }

    return null;
  }, []);

  const handleTranscript = useCallback((data) => {
    const transcript = {
      id: data.id || Date.now(),
      text: data.text || data.transcript,
      // Normalize incoming timestamps to millisecond epoch numbers
      timestamp: toTimestampMs(data.timestamp) ?? Date.now(),
      confidence: data.confidence || 0,
      isFinal: data.isFinal !== false,
      speaker: data.speaker || "Speaker",
      type: "transcript",
    };

    // Check if this is a question
    const isQuestion =
      /[?]/.test(transcript.text) ||
      /\b(what|how|when|where|why|who|which|can you|could you|would you|do you|is it|are you|will you|should|provide|suggest|recommend|tell me|explain|help)\b/i.test(
        transcript.text
      );

    if (isQuestion) {
      setLastQuestion(transcript.text);
      // questionTimestamp should be a millisecond epoch or ISO parsable value
      setQuestionTimestamp(new Date(transcript.timestamp).getTime());
      // Keep existing suggestions, don't clear them
    }

    setTranscripts((prev) => {
      // Avoid duplicates
      const isDuplicate = prev.some((t) => {
        const sameText = t.text?.trim() === transcript.text?.trim();
        const timeDiff = Math.abs(
          new Date(t.timestamp) - new Date(transcript.timestamp)
        );
        return sameText && timeDiff < 5000;
      });

      if (isDuplicate) return prev;

      const newTranscripts = [...prev, transcript];
      setSessionStats((s) => ({ ...s, transcripts: newTranscripts.length }));
      return newTranscripts;
    });
  }, [toTimestampMs]);

  /**
   * Handle incoming AI suggestion with structured response support
   */
  const handleSuggestion = useCallback((data) => {
    const suggestionText = data.text || data.suggestion;
    if (!suggestionText || suggestionText.trim() === "") return;

    const suggestion = {
      id: data.id || Date.now(),
      text: suggestionText,
      timestamp: toTimestampMs(data.timestamp) || Date.now(),
      confidence: data.confidence || 0.8,
      type: "suggestion",
      category: data.needs_help ? "urgent" : data.category || "general",
      needsHelp: data.needs_help || false,
      lastQuestion: data.last_question || lastQuestion,
      contextWindow: Array.isArray(data.context_window) ? data.context_window : [],
      questionHash: data.question_hash,
      dialogueLen: data.dialogue_len,
      // Structured response fields
      structuredResponse: data.structured_response || null,
      internalKnowledge: data.structured_response?.internal_knowledge || null,
      externalKnowledge: data.structured_response?.external_knowledge || null,
      metadata: data.structured_response?.metadata || null,
    };

    setSuggestions((prev) => {
      // Avoid duplicates — prefer hash match, fall back to text+time
      const isDuplicate = prev.some((s) => {
        if (suggestion.questionHash && s.questionHash)
          return s.questionHash === suggestion.questionHash;
        const sameText = s.text?.trim() === suggestion.text?.trim();
        const timeDiff = Math.abs(
          new Date(s.timestamp) - new Date(suggestion.timestamp)
        );
        return sameText && timeDiff < 10000;
      });

      if (isDuplicate) return prev;
      return [...prev, suggestion];
    });

    // Show toast notification for urgent suggestions
    if (data.needs_help) {
      setCurrentSuggestion(suggestionText);
      setShowSuggestion(true);

      // Auto-hide after 8 seconds
      setTimeout(() => {
        setShowSuggestion(false);
      }, 8000);
    }
  }, [lastQuestion, toTimestampMs]);

  /**
   * Handle incoming follow-up question suggestion
   */
  const handleFollowUpQuestion = useCallback((data) => {
    const questionText = data.text || data.question;
    if (!questionText || questionText.trim() === "") return;

    const followUpQuestion = {
      id: data.id || Date.now(),
      text: questionText,
      timestamp: toTimestampMs(data.timestamp) || Date.now(),
      confidence: data.confidence || 0.8,
      type: "followup_question",
      category: data.category || "general",
      previousQuestion: data.previous_question || lastQuestion,
      contextWindow: Array.isArray(data.context_window) ? data.context_window : [],
      questionHash: data.question_hash,
      metadata: data.metadata || null,
    };

    setFollowUpQuestions((prev) => {
      // Avoid duplicates — prefer hash match, fall back to text+time
      const isDuplicate = prev.some((q) => {
        if (followUpQuestion.questionHash && q.questionHash)
          return q.questionHash === followUpQuestion.questionHash;
        const sameText = q.text?.trim() === followUpQuestion.text?.trim();
        const timeDiff = Math.abs(
          new Date(q.timestamp) - new Date(followUpQuestion.timestamp)
        );
        return sameText && timeDiff < 10000;
      });

      if (isDuplicate) return prev;
      return [...prev, followUpQuestion];
    });
  }, [lastQuestion, toTimestampMs]);

  const adjustFontSize = useCallback((panel, delta) => {
    setFontSizes((prev) => ({
      ...prev,
      [panel]: Math.min(24, Math.max(10, prev[panel] + delta)),
    }));
  }, []);

  /**
   * Stop capture and cleanup Azure Speech SDK
   */
  const stopCapture = useCallback(async () => {
    try {
      setIsCapturing(false);

      // Stop Azure Speech Recognizer
      if (recognizerRef.current) {
        recognizerRef.current.stopTranscribingAsync(
          () => {
            console.log("✅ Azure Speech recognizer stopped");
            recognizerRef.current.close();
            recognizerRef.current = null;
          },
          (error) => {
            console.error("❌ Error stopping recognizer:", error);
            if (recognizerRef.current) {
              recognizerRef.current.close();
              recognizerRef.current = null;
            }
          }
        );
      }

      // Stop all tracks
      if (displayStreamRef.current) {
        displayStreamRef.current.getTracks().forEach((track) => track.stop());
        displayStreamRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }
      if (combinedStreamRef.current) {
        combinedStreamRef.current.getTracks().forEach((track) => track.stop());
        combinedStreamRef.current = null;
      }

      // Close WebSocket
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Clear video preview
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      stopSessionTimer();
      setConnectionStatus("disconnected");
      setAudioLevel(0);

      // Call end meeting API to generate summary.
      // Build the transcript text HERE (after WS close) from the ref so the
      // backend has it even if meeting_audio_streams was already cleaned up by on_close.
      const transcriptText = transcriptsRef.current
        .filter((t) => t.text?.trim())
        .map((t) => (t.speaker ? `${t.speaker}: ${t.text}` : t.text))
        .join("\n");

      console.log("[ScreenCaptureWeb] Calling end meeting API with:", {
        meetingId,
        userId,
        transcriptLength: transcriptText.length,
      });

      if (meetingId && userId) {
        try {
          setIsGeneratingSummary(true);

          const response = await apiService.endMeeting(meetingId, userId, transcriptText || undefined);
          console.log("[ScreenCaptureWeb] End meeting response:", response);

          // Use the backend's effective_meeting_id so sub-session summaries land
          // on the correct page (e.g. "my-meeting-3" instead of "my-meeting").
          const summaryMeetingId = response?.effective_meeting_id || meetingId;

          if (response && response.status === "success") {
            // Navigate to summary page with the summary data
            setTimeout(() => {
              navigate(`/meeting-summary/${summaryMeetingId}`, {
                state: { summaryData: response },
              });
              setIsGeneratingSummary(false);
            }, 1000);
          } else {
            console.error("[ScreenCaptureWeb] End meeting returned non-success:", response);
            // Still navigate to summary page — it can show whatever is in the DB
            setTimeout(() => {
              navigate(`/meeting-summary/${summaryMeetingId}`);
              setIsGeneratingSummary(false);
            }, 1000);
          }
        } catch (summaryError) {
          console.error("❌ Error generating meeting summary:", summaryError);
          // Navigate anyway so the host isn't stuck on /new-meeting
          navigate(`/meeting-summary/${meetingId}`);
          setIsGeneratingSummary(false);
        }
      } else {
        console.log(
          "[ScreenCaptureWeb] Not calling end meeting API - missing:",
          {
            meetingIdMissing: !meetingId,
            userIdMissing: !userId,
          }
        );
      }

      // Reset meeting ID for next session
      setMeetingId(null);
    } catch (error) {
      console.error("❌ Error stopping capture:", error);
    }
  }, [meetingId, userId, navigate]);
  stopCaptureRef.current = stopCapture;

  /**
   * Clear Session — resets all in-meeting data and starts a new sub-session
   * while keeping the WebSocket and audio capture alive. Only available when
   * cold-call mode is enabled and the user is the meeting host.
   */
  const clearSession = useCallback(async (newProspectName) => {
    if (clearingSession || !meetingId || !userId) return;
    setClearingSession(true);
    try {
      const res = await apiService.clearSession(meetingId, userId, newProspectName);
      if (res?.status !== "success") throw new Error(res?.message || "clear_failed");

      // The backend already broadcast session_cleared + company_reset to all
      // connections (including this one) — transcript/suggestion state is
      // applied locally here for the host's instant feedback, but company
      // name / research state is intentionally left to the company_reset WS
      // handler so there's exactly one code path that sets it, for every client.
      setSessionNumber(res.session_number ?? (sessionNumber + 1));
      setTranscripts([]);
      setLiveTranscripts([]);
      setSuggestions([]);
      setFollowUpQuestions([]);
      setCapabilityHighlights([]);
      setLastQuestion("");
      setSpeakerColors({});
      setSessionStats({ transcripts: 0, duration: 0 });
      transcriptsRef.current = [];
      sessionStartTimeRef.current = Date.now();
      console.log(`📞 [CLEAR SESSION] Session ${res.session_number} started for meeting ${meetingId}`);
    } catch (err) {
      console.error("❌ Clear session failed:", err);
      alert(`Failed to clear session: ${err?.message || "Unknown error"}. Please try again.`);
    } finally {
      setClearingSession(false);
      setShowClearSessionModal(false);
      setClearSessionProspectInput("");
    }
  }, [clearingSession, meetingId, userId, sessionNumber]);

  /**
   * Start session timer
   */
  const startSessionTimer = useCallback(() => {
    statsIntervalRef.current = setInterval(() => {
      if (sessionStartTimeRef.current) {
        const duration = Math.floor(
          (Date.now() - sessionStartTimeRef.current) / 1000
        );
        setSessionStats((prev) => ({ ...prev, duration }));
      }
    }, 1000);
  }, []);

  /**
   * Stop session timer
   */
  const stopSessionTimer = useCallback(() => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
  }, []);

  /**
   * Join an already-active meeting as a passive participant — no screen share,
   * no microphone, no Azure Speech. The WS opens but the user sees a waiting
   * screen until the host accepts. If accepted, the `participant_approved`
   * message handler promotes them into the full meeting UI.
   *
   * Declared as a plain const (not useCallback) so it re-binds each render and
   * always sees the latest `userId` / `connectWebSocket`. A useCallback with
   * stale deps would freeze the captured userId at `null` (it loads async from
   * MSAL after first render) and block the WS connect.
   */
  const joinAsParticipant = (meetingIdToJoin) => {
    setMeetingId(meetingIdToJoin);
    setShowMeetingModal(false);
    setDeclinedMessage(null);
    // Show "Joining meeting…" optimistically. The backend responds immediately:
    // participant_approved (auto-admit for returning/invite users) or
    // approval_pending (switches text to "Waiting for host to admit you…").
    setIsInviteJoining(true);
    setIsPendingApproval(true);
    connectWebSocket(meetingIdToJoin, /* asParticipant */ true);
  };

  /**
   * Host-approval gate: tear everything down when the request is canceled,
   * declined, or times out. Closes the WS without entering the meeting UI.
   */
  const cancelPendingJoin = () => {
    if (websocketRef.current) {
      try { websocketRef.current.close(); } catch { /* ignore */ }
      websocketRef.current = null;
    }
    setIsPendingApproval(false);
    setIsInviteJoining(false);
    setMeetingId(null);
  };

  const cancelWaitingForHost = () => {
    if (waitingForHostIntervalRef.current) {
      clearInterval(waitingForHostIntervalRef.current);
      waitingForHostIntervalRef.current = null;
    }
    setIsWaitingForHostToStart(false);
    setMeetingId(null);
    inviteAutoJoinedRef.current = false;
  };

  /**
   * Host-side: send Accept/Decline to the backend for a pending request.
   */
  const respondToJoinRequest = (connectionId, accept) => {
    const ws = websocketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({
      type: accept ? "participant_approve" : "participant_decline",
      connection_id: connectionId,
    }));
    // Optimistically drop from both lists; the server will confirm with
    // participant_request_resolved (and a participant_join broadcast on accept).
    setPendingRequests((prev) => prev.filter((p) => p.connection_id !== connectionId));
    setPendingToasts((prev) => prev.filter((p) => p.connection_id !== connectionId));
  };

  /**
   * Generate meeting summary
   */
  const generateSummary = useCallback(async () => {
    if (!meetingId) {
      return;
    }

    try {
      setIsGeneratingSummary(true);

      const summaryData = await apiService.generateMeetingSummary(meetingId);

      // Navigate to summary page with complete data
      setTimeout(() => {
        navigate(`/meeting-summary/${meetingId}`, { state: { summaryData } });
      }, 1000);
    } catch (error) {
      console.error("❌ Failed to generate summary:", error);
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [meetingId, navigate]);

  /**
   * Cleanup resources on unmount
   */
  useEffect(() => {
    return () => {
      // Stop recognizer
      if (recognizerRef.current) {
        recognizerRef.current.close();
      }

      // Stop all media tracks
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (displayStreamRef.current) {
        displayStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (combinedStreamRef.current) {
        combinedStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Close WebSocket
      if (websocketRef.current) {
        websocketRef.current.close();
      }

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Stop session timer
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }

      // Stop waiting-for-host polling
      if (waitingForHostIntervalRef.current) {
        clearInterval(waitingForHostIntervalRef.current);
        waitingForHostIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isCapturing) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isCapturing]);

  /**
   * Format duration
   */
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  /**
   * Copy text to clipboard
   */
  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  /** Copy the whole pitch as plain text the rep can paste anywhere. */
  const copyPitchToClipboard = useCallback(
    (pitch) => {
      const text = pitchToPlainText(pitch);
      if (text) copyToClipboard(text);
    },
    [copyToClipboard]
  );

  /**
   * Rebuild the pitch from the research already persisted for this meeting.
   * The backend answers 202 and streams pitch_progress / pitch_complete over
   * the meeting websocket, so there is nothing to read from the response body.
   */
  const regeneratePitch = useCallback(async () => {
    if (!meetingId) return;
    setPitchStatus("running");
    setPitchStep("Rebuilding the pitch…");
    setPitchProgress(0);
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const res = await fetch(
        `${baseURL}/meeting_pitch/${encodeURIComponent(meetingId)}`,
        { method: "POST" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `HTTP ${res.status}`);
      }
    } catch (err) {
      console.warn("[PITCH] regenerate failed:", err);
      setPitchStatus("error");
      setPitchStep("Could not start pitch generation. Please try again.");
    }
  }, [meetingId]);

  return (
    <div
      style={{
        background: "#F5F3FF",
        overflow: isCapturing ? "hidden" : undefined,
      }}
    >
      <div className={`mx-auto ${isCapturing ? "px-5 pt-3 pb-0" : "p-5"}`}>
        <div style={{ background: "#F5F3FF" }}>
          {/* Meeting ID Modal */}
          {showMeetingModal && (
            <MeetingIdModal
              isOpen={showMeetingModal}
              onConfirm={handleMeetingIdConfirm}
              onClose={() => {
                setShowMeetingModal(false);
              }}
            />
          )}

          {/* Initialization Loader */}
          {isInitializing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white border border-purple-200 rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4">
                <div className="flex flex-col items-center">
                  {/* Animated spinner */}
                  <div className="relative w-20 h-20 mb-6">
                    <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                    <div
                      className="absolute inset-0 border-4 border-transparent rounded-full animate-spin"
                      style={{ borderTopColor: "#e04665" }}
                    ></div>
                    <div
                      className="absolute inset-2 border-4 border-transparent border-t-purple-400 rounded-full animate-spin"
                      style={{ animationDuration: "1.5s" }}
                    ></div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Initializing Recording
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-center text-sm mb-4">
                    Setting up speech recognition and speaker identification...
                  </p>

                  {/* Progress steps */}
                  <div className="w-full space-y-2">
                    <div className="flex items-center gap-3 text-sm">
                      <div
                        className="w-2 h-2 rounded-full animate-pulse"
                        style={{ backgroundColor: "#e04665" }}
                      ></div>
                      <span className="text-gray-700">
                        Fetching authentication token
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div
                        className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <span className="text-gray-700">
                        Requesting media permissions
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div
                        className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"
                        style={{ animationDelay: "0.4s" }}
                      ></div>
                      <span className="text-gray-700">
                        Configuring audio streams
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className="max-w-[1800px] mx-auto"
            style={{ paddingBottom: isCapturing ? 0 : "32px" }}
          >
            {/* Centered Capture Options - Before Meeting */}
            {!isCapturing && (
              <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
                <div className="w-full max-w-xl">
                  <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 border-2 border-purple-300 rounded-2xl p-8 shadow-lg">
                    {/* Welcome Header */}
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-4 shadow-lg">
                        <Monitor className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        Ready to Start?
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Configure your audio sources and begin capturing your meeting
                      </p>
                    </div>

                    {/* Capture Options */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-purple-100 mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Mic className="w-4 h-4" style={{ color: "#e04665" }} />
                        Audio Sources
                      </h4>
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer group p-3 rounded-lg hover:bg-purple-50 transition-all">
                          <input
                            type="checkbox"
                            checked={captureAudio}
                            onChange={(e) => setCaptureAudio(e.target.checked)}
                            disabled={isCapturing}
                            className="w-5 h-5 rounded bg-white border-2 border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-800 group-hover:text-gray-900 transition-colors block">
                              System Audio
                            </span>
                            <span className="text-xs text-gray-500">
                              Capture audio from your computer
                            </span>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer group p-3 rounded-lg hover:bg-purple-50 transition-all">
                          <input
                            type="checkbox"
                            checked={captureMicrophone}
                            onChange={(e) =>
                              setCaptureMicrophone(e.target.checked)
                            }
                            disabled={isCapturing}
                            className="w-5 h-5 rounded bg-white border-2 border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-800 group-hover:text-gray-900 transition-colors block">
                              Microphone
                            </span>
                            <span className="text-xs text-gray-500">
                              Capture your voice input
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Start Capture Button */}
                    <button
                      onClick={startCapture}
                      disabled={!userId}
                      className="w-full px-6 py-4 text-white rounded-xl transition-all font-semibold text-base flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      style={{ background: userId ? "linear-gradient(135deg, #e04665 0%, #d94557 100%)" : "#9ca3af" }}
                    >
                      <Play size={20} />
                      {userId ? "Start Meeting Capture" : "Loading..."}
                    </button>
                    
                    {/* Info Footer */}
                    <div className="mt-6 pt-5 border-t border-purple-200">
                      <div className="flex items-start gap-3 text-xs text-gray-600">
                        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-blue-600 font-bold">i</span>
                        </div>
                        <p className="leading-relaxed">
                          AI will transcribe conversations in real-time and provide intelligent suggestions based on the discussion
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Panel Visibility Toolbar */}
            {isCapturing && (
              <div className="flex items-center gap-1.5 mb-2 flex-shrink-0 flex-wrap">
                {[
                  { id: "followUp", label: "Follow-up", icon: HelpCircle },
                  { id: "suggestions", label: "AI Suggestions", icon: Lightbulb },
                  { id: "engineering", label: "Engineering", icon: GitBranch },
                  { id: "relevantCards", label: "Relevant Cards", icon: Layers },
                  { id: "transcript", label: "Transcript", icon: MessageSquare },
                  { id: "insights", label: "Insights", icon: Building2 },
                ].map(({ id, label, icon: Icon }) => {
                  const active =
                    id === "transcript"
                      ? showTranscript
                      : id === "insights"
                      ? showResearch
                      : panelVisible[id];
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        if (id === "transcript") setShowTranscript((v) => !v);
                        else if (id === "insights") setShowResearch((v) => !v);
                        else togglePanelVisible(id);
                      }}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                        active
                          ? "bg-purple-100 text-purple-700 border-purple-300"
                          : "bg-white text-gray-400 border-gray-200 hover:text-gray-600"
                      }`}
                      title={`${active ? "Hide" : "Show"} ${label}`}
                    >
                      <Icon size={11} />
                      {label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={resetPanelLayout}
                  className="ml-auto text-[11px] text-gray-400 hover:text-gray-600 px-2 py-1 rounded border border-gray-200 bg-white transition-colors"
                  title="Reset panel order and visibility to default"
                >
                  <LayoutGrid size={11} className="inline mr-1" />
                  Reset layout
                </button>
              </div>
            )}

            {/* Meeting Panels - During Meeting */}
            {isCapturing && (() => {
              const visiblePanels = panelOrder.filter((id) =>
                id === "transcript"
                  ? showTranscript
                  : id === "insights"
                  ? showResearch
                  : panelVisible[id]
              );
              return (
                <PanelGroup
                  direction="horizontal"
                  autoSaveId="meeting-panels-v1"
                  style={{ height: "calc(100vh - 185px)" }}
                >
                  {visiblePanels.map((panelId, index) => (
                    <React.Fragment key={panelId}>
                      {index > 0 && (
                        <PanelResizeHandle className="group w-3 flex items-center justify-center cursor-col-resize relative">
                          <div className="w-px h-full bg-gray-200 group-hover:bg-purple-400 group-data-[resize-handle-active=pointer]:bg-purple-500 transition-colors" />
                          <div className="absolute flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-1 h-1 rounded-full bg-gray-500" />
                            <div className="w-1 h-1 rounded-full bg-gray-500" />
                            <div className="w-1 h-1 rounded-full bg-gray-500" />
                          </div>
                        </PanelResizeHandle>
                      )}
                      <Panel
                        id={panelId}
                        defaultSize={Math.floor(100 / visiblePanels.length)}
                        minSize={12}
                        className="flex flex-col overflow-hidden"
                      >
                          {panelId === "followUp" && (
                <div className="flex-1 basis-0 min-w-0 flex flex-col min-h-0">
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-blue-600" />
                      Follow-up Questions
                      {followUpQuestions.length > 0 && (
                        <span className="text-xs font-normal text-gray-500">
                          ({followUpQuestions.length})
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-1 text-gray-500">
                      <button
                        type="button"
                        onClick={() => adjustFontSize("followUp", -1)}
                        className="p-1 hover:text-gray-900"
                        title="Decrease font size"
                        aria-label="Decrease font size"
                      >
                        <span className="text-xs font-bold">A-</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustFontSize("followUp", 1)}
                        className="p-1 hover:text-gray-900"
                        title="Increase font size"
                        aria-label="Increase font size"
                      >
                        <span className="text-xs font-bold">A+</span>
                      </button>
                    </div>
                  </div>
                  <div
                    className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-2"
                    style={{ fontSize: `${fontSizes.followUp}px` }}
                  >
                    {followUpQuestions.length === 0 ? (
                      <div className="text-gray-500 italic py-6">
                        Follow-up questions will appear here.
                      </div>
                    ) : (
                      followUpQuestions
                        .slice()
                        .reverse()
                        .map((question, index) => (
                          <div
                            key={question.id || index}
                            className="group relative py-3 border-b border-gray-100"
                          >
                            {question.contextWindow && question.contextWindow.length > 0 ? (
                              <details className="group/ctx mb-2 rounded-md bg-gray-50 border border-gray-200">
                                <summary className="flex items-center justify-between cursor-pointer select-none px-2 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 hover:text-gray-700">
                                  <span>
                                    Based on conversation ({question.contextWindow.length})
                                  </span>
                                  <ChevronDown
                                    size={12}
                                    className="transition-transform group-open/ctx:rotate-180"
                                  />
                                </summary>
                                <div className="px-2 pb-2 space-y-0.5 border-t border-gray-200">
                                  {question.contextWindow.map((m, i) => (
                                    <div key={i} className="text-xs text-gray-700 leading-snug pt-1">
                                      <span className="font-medium text-gray-900">{m.speaker}:</span>{" "}
                                      <span className="italic">{m.text}</span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            ) : question.previousQuestion ? (
                              <div className="text-xs text-gray-500 italic mb-1">
                                Re: "{question.previousQuestion.substring(0, 60)}..."
                              </div>
                            ) : null}
                            <div className="text-gray-900 pr-14 leading-snug whitespace-pre-wrap">
                              {question.text}
                            </div>
                            <div className="absolute top-2 right-0 flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => copyToClipboard(question.text)}
                                className="p-1 text-gray-400 hover:text-gray-900 opacity-60 hover:opacity-100 transition-opacity"
                                title="Copy"
                                aria-label="Copy"
                              >
                                <Copy size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setFollowUpQuestions((prev) =>
                                    prev.filter(
                                      (q) =>
                                        (q.id ?? q.text) !==
                                        (question.id ?? question.text)
                                    )
                                  )
                                }
                                className="p-1 text-gray-400 hover:text-red-600 opacity-60 hover:opacity-100 transition-opacity"
                                title="Remove"
                                aria-label="Remove"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
                          )}
                          {panelId === "suggestions" && (
                <div className="flex-1 basis-0 min-w-0 flex flex-col min-h-0">
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      AI Suggestions
                      {suggestions.length > 0 && (
                        <span className="text-xs font-normal text-gray-500">
                          ({suggestions.length})
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-1 text-gray-500">
                      <button
                        type="button"
                        onClick={triggerManualSuggestion}
                        disabled={
                          manualTriggerInFlight ||
                          manualCooldownMs > 0 ||
                          connectionStatus !== "connected"
                        }
                        className={`flex items-center gap-1 px-2 py-1 mr-1 rounded text-xs font-medium transition-colors ${
                          manualTriggerInFlight ||
                          manualCooldownMs > 0 ||
                          connectionStatus !== "connected"
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                        }`}
                        title={
                          connectionStatus !== "connected"
                            ? "Connect to start a meeting first"
                            : manualTriggerInFlight
                            ? "Generating suggestion..."
                            : manualCooldownMs > 0
                            ? `Wait ${Math.ceil(manualCooldownMs / 1000)}s`
                            : "Generate suggestion now"
                        }
                        aria-label="Generate suggestion now"
                      >
                        <Sparkles size={12} />
                        <span>
                          {manualTriggerInFlight
                            ? "Generating..."
                            : manualCooldownMs > 0
                            ? `${Math.ceil(manualCooldownMs / 1000)}s`
                            : "Generate now"}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustFontSize("suggestions", -1)}
                        className="p-1 hover:text-gray-900"
                        title="Decrease font size"
                        aria-label="Decrease font size"
                      >
                        <span className="text-xs font-bold">A-</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustFontSize("suggestions", 1)}
                        className="p-1 hover:text-gray-900"
                        title="Increase font size"
                        aria-label="Increase font size"
                      >
                        <span className="text-xs font-bold">A+</span>
                      </button>
                    </div>
                  </div>
                  {manualTriggerError && (
                    <div className="text-xs text-red-600 mb-2 flex-shrink-0">
                      {manualTriggerError}
                    </div>
                  )}
                  <div
                    ref={suggestionsContainerRef}
                    className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-2"
                    style={{ fontSize: `${fontSizes.suggestions}px` }}
                  >
                    {suggestions.length === 0 ? (
                      <div className="text-gray-500 italic py-6">
                        {lastQuestion
                          ? "Waiting for suggestions to your question..."
                          : "AI suggestions will appear here when you ask questions."}
                      </div>
                    ) : (
                      suggestions
                        .slice()
                        .reverse()
                        .map((suggestion, index) => {
                          const text =
                            suggestion.text ||
                            suggestion.suggestion ||
                            (typeof suggestion === "string" ? suggestion : "");
                          const internalMatch = text.match
                            ? text.match(/📚 Internal Knowledge:(.*?)(?=🌐|$)/s)
                            : null;
                          const externalMatch = text.match
                            ? text.match(/🌐 Web Search:(.*?)(?=🌐 Web Sources:|$)/s)
                            : null;
                          const sourcesMatch = text.match
                            ? text.match(/🌐 Web Sources:(.*?)$/s)
                            : null;
                          const internalText = internalMatch?.[1]?.trim();
                          const externalText = externalMatch?.[1]?.trim();
                          const sourcesText = sourcesMatch?.[1]?.trim();

                          const makeUrlsClickable = (s) => {
                            if (!s) return null;
                            const urlRegex = /(https?:\/\/[^\s]+)/g;
                            return s.split(urlRegex).map((part, idx) =>
                              part.match(urlRegex) ? (
                                <a
                                  key={idx}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-700 underline"
                                >
                                  {part}
                                </a>
                              ) : (
                                <span key={idx}>{part}</span>
                              )
                            );
                          };

                          return (
                            <div
                              key={suggestion.id || index}
                              className="group relative py-4 border-b border-gray-100"
                            >
                              <div className="absolute top-3 right-0 flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(text)}
                                  className="p-1 text-gray-400 hover:text-gray-900 opacity-60 hover:opacity-100 transition-opacity"
                                  title="Copy"
                                  aria-label="Copy"
                                >
                                  <Copy size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSuggestions((prev) =>
                                      prev.filter(
                                        (s) =>
                                          (s.id ?? s.text ?? s) !==
                                          (suggestion.id ??
                                            suggestion.text ??
                                            suggestion)
                                      )
                                    )
                                  }
                                  className="p-1 text-gray-400 hover:text-red-600 opacity-60 hover:opacity-100 transition-opacity"
                                  title="Remove"
                                  aria-label="Remove"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2 pr-14">
                                <span>
                                  {suggestion.timestamp
                                    ? new Date(suggestion.timestamp).toLocaleTimeString()
                                    : "Now"}
                                </span>
                                {suggestion.needsHelp && (
                                  <span className="text-red-600 font-medium">urgent</span>
                                )}
                                {suggestion.metadata?.overall_confidence != null && (
                                  <span className="text-gray-500">
                                    {Math.round(suggestion.metadata.overall_confidence)}%
                                  </span>
                                )}
                              </div>

                              {suggestion.contextWindow && suggestion.contextWindow.length > 0 ? (
                                <details className="group/ctx mb-2 mr-14 rounded-md bg-gray-50 border border-gray-200">
                                  <summary className="flex items-center justify-between cursor-pointer select-none px-2 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 hover:text-gray-700">
                                    <span>
                                      Based on conversation ({suggestion.contextWindow.length})
                                    </span>
                                    <ChevronDown
                                      size={12}
                                      className="transition-transform group-open/ctx:rotate-180"
                                    />
                                  </summary>
                                  <div className="px-2 pb-2 space-y-0.5 border-t border-gray-200">
                                    {suggestion.contextWindow.map((m, i) => (
                                      <div key={i} className="text-xs text-gray-700 leading-snug pt-1">
                                        <span className="font-medium text-gray-900">{m.speaker}:</span>{" "}
                                        <span className="italic">{m.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              ) : suggestion.lastQuestion ? (
                                <div className="text-gray-600 italic mb-2 pr-14 leading-snug">
                                  Q: "{suggestion.lastQuestion}"
                                </div>
                              ) : null}

                              {suggestion.structuredResponse ? (
                                <>
                                  {suggestion.internalKnowledge &&
                                    suggestion.internalKnowledge.confidence > 0 &&
                                    suggestion.internalKnowledge.answer &&
                                    !suggestion.internalKnowledge.answer
                                      .toLowerCase()
                                      .includes("no relevant information") && (
                                      <div className="mb-2">
                                        <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                                          <BookOpen className="w-3 h-3" />
                                          Internal Knowledge
                                          {suggestion.internalKnowledge.confidence > 0 && (
                                            <span className="text-gray-400 font-normal">
                                              ({Math.round(suggestion.internalKnowledge.confidence)}%)
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                                          {suggestion.internalKnowledge.answer}
                                        </div>
                                        {suggestion.internalKnowledge.citations &&
                                          suggestion.internalKnowledge.citations.length > 0 && (
                                            <div className="mt-1 text-xs text-gray-500">
                                              {suggestion.internalKnowledge.citations
                                                .slice(0, 3)
                                                .map((citation, idx) => (
                                                  <div
                                                    key={idx}
                                                    className="whitespace-pre-wrap mt-1"
                                                  >
                                                    {citation}
                                                  </div>
                                                ))}
                                            </div>
                                          )}
                                      </div>
                                    )}

                                  {suggestion.externalKnowledge &&
                                    suggestion.externalKnowledge.answer && (
                                      <div className="mb-2">
                                        <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                                          <Globe className="w-3 h-3" />
                                          Web Search
                                          {suggestion.externalKnowledge.confidence > 0 && (
                                            <span className="text-gray-400 font-normal">
                                              ({Math.round(suggestion.externalKnowledge.confidence)}%)
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                                          {suggestion.externalKnowledge.answer}
                                        </div>
                                        {suggestion.externalKnowledge.citations &&
                                          suggestion.externalKnowledge.citations.length > 0 && (
                                            <div className="mt-1 text-xs">
                                              {suggestion.externalKnowledge.citations.map(
                                                (citation, idx) => {
                                                  const cLines = citation.split("\n");
                                                  const title = cLines[0]
                                                    ?.replace(/^\[\d+\]\s*/, "")
                                                    .trim();
                                                  const url = cLines[1]?.trim();
                                                  if (!title || !url) return null;
                                                  return (
                                                    <a
                                                      key={idx}
                                                      href={url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="flex items-center gap-1 text-blue-700 hover:underline mt-1"
                                                    >
                                                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                                      <span className="truncate">{title}</span>
                                                    </a>
                                                  );
                                                }
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    )}
                                </>
                              ) : (
                                <>
                                  {internalText &&
                                    !internalText
                                      .toLowerCase()
                                      .includes("no relevant information") && (
                                      <div className="mb-2">
                                        <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                                          <BookOpen className="w-3 h-3" />
                                          Internal Knowledge
                                        </div>
                                        <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                                          {makeUrlsClickable(internalText)}
                                        </div>
                                      </div>
                                    )}

                                  {externalText && (
                                    <div className="mb-2">
                                      <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                                        <Globe className="w-3 h-3" />
                                        Web Search
                                      </div>
                                      <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                                        {makeUrlsClickable(externalText)}
                                      </div>
                                    </div>
                                  )}

                                  {sourcesText && (
                                    <div className="text-xs mt-1">
                                      {sourcesText
                                        .split(/\[\d+\]/)
                                        .filter(Boolean)
                                        .map((source, idx) => {
                                          const sLines = source.trim().split("\n");
                                          const title = sLines[0]?.trim();
                                          const url = sLines[1]?.trim();
                                          if (!title || !url) return null;
                                          return (
                                            <a
                                              key={idx}
                                              href={url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex items-center gap-1 text-blue-700 hover:underline mt-1"
                                            >
                                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                              <span className="truncate">{title}</span>
                                            </a>
                                          );
                                        })}
                                    </div>
                                  )}

                                  {!internalText && !externalText && !sourcesText && (
                                    <div className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                                      {makeUrlsClickable(text)}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
                          )}
                          {panelId === "engineering" && (
                            <EngineeringPracticesPanel
                              highlights={capabilityHighlights}
                              fontSize={fontSizes.engineering}
                              onAdjustFont={adjustFontSize}
                              onCopyToClipboard={copyToClipboard}
                              connectionStatus={connectionStatus}
                            />
                          )}
                          {panelId === "relevantCards" && (
                            <RelevantCardsPanel
                              cards={relevantCards}
                              fontSize={fontSizes.relevantCards}
                              onAdjustFont={adjustFontSize}
                              onCopyToClipboard={copyToClipboard}
                            />
                          )}
                          {panelId === "transcript" && (
                  <div className="flex-1 basis-0 min-w-0 flex flex-col min-h-0">
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 flex-shrink-0">
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-700" />
                        Transcript
                        {transcripts.length > 0 && (
                          <span className="text-xs font-normal text-gray-500">
                            ({transcripts.length})
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-1 text-gray-500">
                        <button
                          type="button"
                          onClick={() => adjustFontSize("transcript", -1)}
                          className="p-1 hover:text-gray-900"
                          title="Decrease font size"
                          aria-label="Decrease font size"
                        >
                          <span className="text-xs font-bold">A-</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => adjustFontSize("transcript", 1)}
                          className="p-1 hover:text-gray-900"
                          title="Increase font size"
                          aria-label="Increase font size"
                        >
                          <span className="text-xs font-bold">A+</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowTranscript(false)}
                          className="p-1 hover:text-gray-900"
                          title="Hide transcript"
                          aria-label="Hide transcript"
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                    <div
                      ref={transcriptContainerRef}
                      className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-2 leading-relaxed"
                      style={{ fontSize: `${fontSizes.transcript}px` }}
                    >
                      {transcripts.length === 0 && liveTranscripts.length === 0 ? (
                        <div className="text-gray-500 italic py-6">
                          Conversation will appear here as people speak.
                        </div>
                      ) : (
                        <>
                          {transcripts.map((t, index) => {
                            const speakerColor = getSpeakerColor(t.speaker);
                            return (
                              <div key={t.id || `t-${index}`} className="mb-1">
                                <span
                                  className="font-semibold"
                                  style={{ color: speakerColor.text }}
                                >
                                  {t.speaker}:
                                </span>{" "}
                                <span className="text-gray-900">{t.text}</span>
                              </div>
                            );
                          })}
                          {liveTranscripts.map((t, index) => {
                            const speakerColor = getSpeakerColor(t.speaker);
                            return (
                              <div
                                key={t.id || `live-${index}`}
                                className="mb-1 italic opacity-70"
                              >
                                <span
                                  className="font-semibold"
                                  style={{ color: speakerColor.text }}
                                >
                                  {t.speaker}:
                                </span>{" "}
                                <span className="text-gray-700">{t.text}</span>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                          )}
                          {panelId === "insights" && (
                  <div className="flex-1 basis-0 min-w-0 flex flex-col min-h-0">
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 flex-shrink-0">
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-indigo-600" />
                        Account Insights
                        {prospectName && (
                          <span className="text-xs font-normal text-gray-500 truncate max-w-[140px]">
                            {prospectName}
                          </span>
                        )}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowResearch(false)}
                        className="p-1 text-gray-500 hover:text-gray-900"
                        title="Hide insights"
                        aria-label="Hide insights"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>

                    {/* Research / Pitch sub-tabs */}
                    <div className="flex items-center gap-1 mb-2 flex-shrink-0">
                      {[
                        { id: "research", label: "Research", icon: Building2, status: researchStatus },
                        { id: "pitch", label: "Pitch", icon: Sparkles, status: pitchStatus },
                      ].map(({ id, label, icon: TabIcon, status }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setInsightsTab(id)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            insightsTab === id
                              ? "bg-indigo-600 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          <TabIcon className="w-3.5 h-3.5" />
                          {label}
                          {status === "running" && (
                            <RefreshCw
                              className={`w-3 h-3 animate-spin ${
                                insightsTab === id ? "text-white" : "text-indigo-600"
                              }`}
                            />
                          )}
                        </button>
                      ))}
                    </div>

                    {insightsTab === "research" && (
                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-2 text-sm">
                      {!prospectName ? (
                        <div className="text-gray-500 italic py-6">
                          No prospect was specified for this meeting. Enter a
                          Prospect / Account name when starting a meeting to see
                          Customer AI Insights here.
                        </div>
                      ) : researchStatus === "running" &&
                        isResearchEmpty(accountResearch) ? (
                        <div className="py-6 space-y-4">
                          <div className="flex items-center gap-2 text-gray-700">
                            <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                            <span>{researchStep || "Researching the account…"}</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500 transition-all duration-500"
                              style={{
                                width: `${Math.round((researchProgress || 0) * 100)}%`,
                              }}
                            />
                          </div>
                          <p className="text-xs text-gray-500">
                            Gathering public information about {prospectName}. This
                            usually takes a few seconds.
                          </p>
                        </div>
                      ) : researchStatus === "error" ? (
                        <div className="py-6 text-red-600 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{researchStep || "Account research failed."}</span>
                        </div>
                      ) : accountResearch ? (
                        <div className="space-y-5 pb-4">
                          {/* Company overview */}
                          {accountResearch.company_overview &&
                            (accountResearch.company_overview.summary ||
                              accountResearch.company_overview.industry) && (
                              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
                                {accountResearch.company_overview.summary && (
                                  <p className="text-gray-800 leading-relaxed mb-2">
                                    {accountResearch.company_overview.summary}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                                  {accountResearch.company_overview.industry && (
                                    <span>
                                      <span className="font-medium text-gray-700">
                                        Industry:
                                      </span>{" "}
                                      {accountResearch.company_overview.industry}
                                    </span>
                                  )}
                                  {accountResearch.company_overview.size && (
                                    <span>
                                      <span className="font-medium text-gray-700">
                                        Size:
                                      </span>{" "}
                                      {accountResearch.company_overview.size}
                                    </span>
                                  )}
                                  {accountResearch.company_overview.hq && (
                                    <span>
                                      <span className="font-medium text-gray-700">
                                        HQ:
                                      </span>{" "}
                                      {accountResearch.company_overview.hq}
                                    </span>
                                  )}
                                  {accountResearch.company_overview.website && (
                                    <a
                                      href={
                                        accountResearch.company_overview.website.startsWith(
                                          "http"
                                        )
                                          ? accountResearch.company_overview.website
                                          : `https://${accountResearch.company_overview.website}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-700 hover:underline flex items-center gap-1"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      Website
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Decision makers */}
                          {accountResearch.decision_makers &&
                            accountResearch.decision_makers.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                  <Users className="w-3.5 h-3.5" />
                                  Decision Makers
                                </div>
                                <div className="space-y-2">
                                  {accountResearch.decision_makers.map((d, i) => (
                                    <div
                                      key={i}
                                      className="border-b border-gray-100 pb-2 last:border-0"
                                    >
                                      <div className="font-medium text-gray-900">
                                        {d.name}
                                        {d.title && (
                                          <span className="font-normal text-gray-500">
                                            {" "}
                                            — {d.title}
                                          </span>
                                        )}
                                      </div>
                                      {d.note && (
                                        <div className="text-gray-600 text-xs mt-0.5">
                                          {d.note}
                                        </div>
                                      )}
                                      {d.source_url && (
                                        <a
                                          href={d.source_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-700 hover:underline text-xs flex items-center gap-1 mt-0.5"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          Source
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Priorities & initiatives */}
                          {accountResearch.priorities &&
                            accountResearch.priorities.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                  <Target className="w-3.5 h-3.5" />
                                  Priorities & Initiatives
                                </div>
                                <div className="space-y-2">
                                  {accountResearch.priorities.map((p, i) => (
                                    <div
                                      key={i}
                                      className="border-b border-gray-100 pb-2 last:border-0"
                                    >
                                      <div className="font-medium text-gray-900">
                                        {p.title}
                                      </div>
                                      {p.detail && (
                                        <div className="text-gray-600 text-xs mt-0.5">
                                          {p.detail}
                                        </div>
                                      )}
                                      {p.source_url && (
                                        <a
                                          href={p.source_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-700 hover:underline text-xs flex items-center gap-1 mt-0.5"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          Source
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Tech landscape */}
                          {accountResearch.tech_landscape &&
                            accountResearch.tech_landscape.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                  <Cpu className="w-3.5 h-3.5" />
                                  Tech Landscape
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {accountResearch.tech_landscape.map((t, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs"
                                      title={
                                        [t.category, t.note]
                                          .filter(Boolean)
                                          .join(" · ") || undefined
                                      }
                                    >
                                      {t.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Sources */}
                          {accountResearch.sources &&
                            accountResearch.sources.length > 0 && (
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                  Sources
                                </div>
                                <div className="space-y-1">
                                  {accountResearch.sources.map((s, i) => (
                                    <a
                                      key={i}
                                      href={s.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-blue-700 hover:underline text-xs"
                                    >
                                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">
                                        {s.title || s.url}
                                      </span>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                          {!accountResearch.company_overview?.summary &&
                            (accountResearch.decision_makers?.length ?? 0) === 0 &&
                            (accountResearch.priorities?.length ?? 0) === 0 &&
                            (accountResearch.tech_landscape?.length ?? 0) === 0 && (
                              <div className="text-gray-500 italic py-6">
                                No public information could be found for{" "}
                                {prospectName}.
                              </div>
                            )}
                        </div>
                      ) : (
                        <div className="text-gray-500 italic py-6">
                          Insights will appear here once research completes.
                        </div>
                      )}
                    </div>
                    )}

                    {insightsTab === "pitch" && (
                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-2 text-sm">
                      {!prospectName ? (
                        <div className="text-gray-500 italic py-6">
                          No prospect was specified for this meeting. Enter a
                          Prospect / Account name when starting a meeting to get a
                          Simform pitch built around them.
                        </div>
                      ) : pitchStatus === "running" &&
                        isPitchEmpty(accountPitch) ? (
                        <div className="py-6 space-y-4">
                          <div className="flex items-center gap-2 text-gray-700">
                            <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                            <span>
                              {pitchStep ||
                                (researchStatus === "running"
                                  ? "Waiting for account research…"
                                  : "Building the pitch…")}
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500 transition-all duration-500"
                              style={{
                                width: `${Math.round((pitchProgress || 0) * 100)}%`,
                              }}
                            />
                          </div>
                          <p className="text-xs text-gray-500">
                            Matching {prospectName} against Simform's case studies and
                            capabilities.
                          </p>
                        </div>
                      ) : pitchStatus === "error" ? (
                        <div className="py-6 space-y-3">
                          <div className="text-red-600 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{pitchStep || "Pitch generation failed."}</span>
                          </div>
                          <button
                            type="button"
                            onClick={regeneratePitch}
                            disabled={researchStatus !== "done"}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Try again
                          </button>
                        </div>
                      ) : accountPitch && !isPitchEmpty(accountPitch) ? (
                        <div className="space-y-5 pb-4">
                          {/* Headline + positioning */}
                          {(accountPitch.headline ||
                            accountPitch.positioning_statement) && (
                            <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
                              {accountPitch.headline && (
                                <p className="font-semibold text-gray-900 mb-1">
                                  {accountPitch.headline}
                                </p>
                              )}
                              {accountPitch.positioning_statement && (
                                <p className="text-gray-800 leading-relaxed">
                                  {accountPitch.positioning_statement}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Why now */}
                          {accountPitch.why_now?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <Zap className="w-3.5 h-3.5" />
                                Why Now
                              </div>
                              <div className="space-y-2">
                                {accountPitch.why_now.map((w, i) => (
                                  <div
                                    key={i}
                                    className="border-b border-gray-100 pb-2 last:border-0"
                                  >
                                    <div className="font-medium text-gray-900">
                                      {w.trigger}
                                    </div>
                                    {w.so_what && (
                                      <div className="text-gray-600 text-xs mt-0.5">
                                        {w.so_what}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Pain hypotheses */}
                          {accountPitch.pain_hypotheses?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <AlertCircle className="w-3.5 h-3.5" />
                                Likely Pains
                              </div>
                              <div className="space-y-2">
                                {accountPitch.pain_hypotheses.map((h, i) => (
                                  <div
                                    key={i}
                                    className="border-b border-gray-100 pb-2 last:border-0"
                                  >
                                    <div className="font-medium text-gray-900">
                                      {h.pain}
                                    </div>
                                    {h.evidence && (
                                      <div className="text-gray-600 text-xs mt-0.5">
                                        {h.evidence}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Value propositions */}
                          {accountPitch.value_propositions?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <Target className="w-3.5 h-3.5" />
                                Value Propositions
                              </div>
                              <div className="space-y-2">
                                {accountPitch.value_propositions.map((v, i) => (
                                  <div
                                    key={i}
                                    className="rounded-md border border-gray-200 p-2.5"
                                  >
                                    <div className="font-medium text-gray-900">
                                      {v.title}
                                    </div>
                                    {v.prospect_need && (
                                      <div className="text-gray-600 text-xs mt-1">
                                        <span className="font-semibold text-gray-500">
                                          Their need:{" "}
                                        </span>
                                        {v.prospect_need}
                                      </div>
                                    )}
                                    {v.simform_capability && (
                                      <div className="text-gray-600 text-xs mt-0.5">
                                        <span className="font-semibold text-gray-500">
                                          Simform:{" "}
                                        </span>
                                        {v.simform_capability}
                                      </div>
                                    )}
                                    {v.outcome && (
                                      <div className="text-gray-700 text-xs mt-0.5">
                                        <span className="font-semibold text-gray-500">
                                          Outcome:{" "}
                                        </span>
                                        {v.outcome}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Proof points */}
                          {accountPitch.proof_points?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <Award className="w-3.5 h-3.5" />
                                Proof Points
                              </div>
                              <div className="space-y-2">
                                {accountPitch.proof_points.map((pp, i) => (
                                  <div
                                    key={i}
                                    className="rounded-md bg-gray-50 border border-gray-200 p-2.5"
                                  >
                                    <div className="font-medium text-gray-900">
                                      {pp.title}
                                    </div>
                                    {pp.relevance && (
                                      <div className="text-gray-600 text-xs mt-0.5">
                                        {pp.relevance}
                                      </div>
                                    )}
                                    {pp.metrics && (
                                      <div className="text-emerald-700 text-xs mt-1 font-medium">
                                        {pp.metrics}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Differentiators */}
                          {accountPitch.differentiators?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <CheckCircle className="w-3.5 h-3.5" />
                                Differentiators
                              </div>
                              <ul className="space-y-1">
                                {accountPitch.differentiators.map((d, i) => (
                                  <li
                                    key={i}
                                    className="text-gray-700 text-xs flex items-start gap-1.5"
                                  >
                                    <span className="text-indigo-500 mt-0.5">•</span>
                                    <span>{d}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Discovery questions */}
                          {accountPitch.discovery_questions?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <HelpCircle className="w-3.5 h-3.5" />
                                Discovery Questions
                              </div>
                              <ul className="space-y-1">
                                {accountPitch.discovery_questions.map((q, i) => (
                                  <li
                                    key={i}
                                    className="text-gray-700 text-xs flex items-start gap-1.5"
                                  >
                                    <span className="text-indigo-500 mt-0.5">{i + 1}.</span>
                                    <span>{q}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Objection handling */}
                          {accountPitch.objection_handling?.length > 0 && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <MessageSquare className="w-3.5 h-3.5" />
                                Objection Handling
                              </div>
                              <div className="space-y-2">
                                {accountPitch.objection_handling.map((o, i) => (
                                  <div
                                    key={i}
                                    className="border-b border-gray-100 pb-2 last:border-0"
                                  >
                                    <div className="font-medium text-gray-900 text-xs">
                                      “{o.objection}”
                                    </div>
                                    {o.response && (
                                      <div className="text-gray-600 text-xs mt-0.5">
                                        {o.response}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Engagement model */}
                          {(accountPitch.engagement_model?.recommended ||
                            accountPitch.engagement_model?.rationale) && (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                <Users className="w-3.5 h-3.5" />
                                Engagement Model
                              </div>
                              <div className="text-gray-800 text-xs">
                                {accountPitch.engagement_model.recommended}
                              </div>
                              {accountPitch.engagement_model.rationale && (
                                <div className="text-gray-600 text-xs mt-0.5">
                                  {accountPitch.engagement_model.rationale}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Call to action */}
                          {accountPitch.call_to_action && (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
                              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">
                                Next Step
                              </div>
                              <div className="text-gray-800 text-xs">
                                {accountPitch.call_to_action}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              type="button"
                              onClick={regeneratePitch}
                              disabled={researchStatus !== "done"}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Rebuild the pitch from the current research"
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                              Regenerate
                            </button>
                            <button
                              type="button"
                              onClick={() => copyPitchToClipboard(accountPitch)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                              title="Copy the pitch as plain text"
                            >
                              <Copy className="w-3.5 h-3.5" />
                              Copy
                            </button>
                          </div>
                        </div>
                      ) : accountPitch ? (
                        <div className="py-6 space-y-3">
                          <div className="text-gray-500 italic">
                            No pitch could be built for {prospectName} — the Simform
                            knowledge base returned nothing relevant to the research.
                          </div>
                          <button
                            type="button"
                            onClick={regeneratePitch}
                            disabled={researchStatus !== "done"}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Regenerate
                          </button>
                        </div>
                      ) : researchStatus === "done" ? (
                        // Research landed but no pitch did — the pitch leg of the
                        // research task failed before it could broadcast, or this
                        // meeting predates the Pitch tab. Offer the manual path.
                        <div className="py-6 space-y-3">
                          <div className="text-gray-500 italic">
                            No pitch has been built for {prospectName} yet.
                          </div>
                          <button
                            type="button"
                            onClick={regeneratePitch}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            Build pitch
                          </button>
                        </div>
                      ) : (
                        <div className="text-gray-500 italic py-6">
                          The pitch appears here once account research completes.
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                          )}
                      </Panel>
                    </React.Fragment>
                  ))}
                </PanelGroup>
              );
            })()}
          </div>

          {/* Floating Pill Dock - Meeting Controls */}
          {isCapturing && (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
              <div className="flex items-end gap-4 px-5 py-2 rounded-full bg-white/90 backdrop-blur-md border border-gray-200 shadow-lg">
                {/* Status dot — green when connected, red when disconnected, blinking either way */}
                <span
                  className={`mb-1.5 w-2.5 h-2.5 rounded-full animate-pulse ${
                    connectionStatus === "connected"
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                  title={
                    connectionStatus === "connected"
                      ? "Connected"
                      : "Disconnected"
                  }
                  aria-label={
                    connectionStatus === "connected"
                      ? "Connected"
                      : "Disconnected"
                  }
                />

                {/* Timer */}
                <div className="flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 leading-none mb-1">
                    Timer
                  </span>
                  <span className="text-sm font-mono text-gray-800 tabular-nums leading-none">
                    {formatDuration(sessionStats.duration)}
                  </span>
                </div>

                {/* Meeting ID */}
                <div className="flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 leading-none mb-1">
                    Meeting ID
                  </span>
                  <span
                    className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full leading-none"
                    title={sessionNumber > 1 ? `${meetingId} · Session ${sessionNumber}` : meetingId}
                  >
                    {sessionNumber > 1 ? `${meetingId}-${sessionNumber}` : meetingId}
                  </span>
                </div>

                {/* Divider */}
                <span className="mb-1 w-px h-7 bg-gray-200" />

                {/* Generate Summary (icon-only, with hover tooltip) */}
                {/* <div className="relative group mb-0.5">
                  <button
                    type="button"
                    onClick={generateSummary}
                    disabled={isGeneratingSummary || transcripts.length === 0}
                    className="p-2 rounded-full text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Generate Summary"
                  >
                    {isGeneratingSummary ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                  </button>
                  <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-white bg-gray-900 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    Generate Summary
                  </span>
                </div> */}

                {/* Participants (info button — opens roster modal) */}
                <div className="relative group mb-0.5">
                  <button
                    type="button"
                    onClick={() => setShowParticipantsModal(true)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors text-xs font-medium ${
                      pendingRequests.length > 0 && !isParticipantMode
                        ? "text-yellow-800 bg-yellow-100 hover:bg-yellow-200"
                        : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                    }`}
                    aria-label="Show participants"
                  >
                    <Info className="w-3.5 h-3.5" />
                    {(participants.length || 1) +
                      (!isParticipantMode ? pendingRequests.length : 0)}
                    {pendingRequests.length > 0 && !isParticipantMode && (
                      <span className="ml-1 px-1 rounded-full bg-yellow-500 text-white text-[10px] leading-4">
                        {pendingRequests.length}
                      </span>
                    )}
                  </button>
                  <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-white bg-gray-900 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    {pendingRequests.length > 0 && !isParticipantMode
                      ? `${pendingRequests.length} pending request${pendingRequests.length > 1 ? "s" : ""}`
                      : "Participants"}
                  </span>
                </div>

                {/* Share invite link (host-only) */}
                {!isParticipantMode && (
                  <div className="relative group mb-0.5">
                    <button
                      type="button"
                      onClick={copyShareLink}
                      disabled={isCopyingLink}
                      className="p-2 rounded-full text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Copy invite link"
                    >
                      {shareLinkCopied
                        ? <CheckCircle className="w-4 h-4 text-green-500" />
                        : <Share2 className="w-4 h-4" />}
                    </button>
                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-white bg-gray-900 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      {shareLinkCopied ? "Link copied!" : "Copy invite link"}
                    </span>
                  </div>
                )}

                {/* Clear Session (cold-call host only) */}
                {isColdCallActive && !isParticipantMode && (
                  <div className="relative group mb-0.5">
                    <button
                      type="button"
                      onClick={() => setShowClearSessionModal(true)}
                      disabled={clearingSession || isGeneratingSummary}
                      className="p-2 rounded-full text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Clear session"
                    >
                      <RefreshCw className={`w-4 h-4 ${clearingSession ? "animate-spin" : ""}`} />
                    </button>
                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-white bg-gray-900 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      Clear session
                    </span>
                  </div>
                )}

                {/* End / Leave Meeting */}
                <div className="relative group mb-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      if (isParticipantMode) {
                        // Participants just leave — no summary, no DB close.
                        if (websocketRef.current) {
                          websocketRef.current.close();
                          websocketRef.current = null;
                        }
                        stopSessionTimer();
                        setIsCapturing(false);
                        setIsParticipantMode(false);
                        setParticipants([]);
                        setConnectionStatus("disconnected");
                      } else {
                        setShowEndConfirm(true);
                      }
                    }}
                    disabled={isGeneratingSummary}
                    className="p-2 rounded-full text-white bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label={isParticipantMode ? "Leave Meeting" : "End Meeting"}
                  >
                    <StopCircle className="w-4 h-4" />
                  </button>
                  <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-white bg-gray-900 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    {isParticipantMode ? "Leave Meeting" : "End Meeting"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Participants Modal — live roster of everyone in the room */}
          {showParticipantsModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Participants
                    <span className="text-sm font-normal text-gray-500">
                      ({participants.length || 0})
                    </span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowParticipantsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                {/* Pending requests — host only (participants don't see this) */}
                {!isParticipantMode && pendingRequests.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                      Pending requests ({pendingRequests.length})
                    </div>
                    <ul className="space-y-2">
                      {pendingRequests.map((req) => {
                        const shortId = req.user_id
                          ? `User ${req.user_id.slice(0, 8)}`
                          : "Unknown user";
                        const name = req.display_name || req.email || shortId;
                        return (
                          <li
                            key={req.connection_id}
                            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="w-2 h-2 rounded-full flex-shrink-0 bg-yellow-500 animate-pulse" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm text-gray-900 font-medium truncate">
                                  {name}
                                </div>
                                {req.email && req.email !== name && (
                                  <div className="text-xs text-gray-500 truncate">
                                    {req.email}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => respondToJoinRequest(req.connection_id, false)}
                                className="px-2 py-1 text-xs font-medium text-gray-700 rounded-md hover:bg-gray-100 border border-gray-200"
                              >
                                Decline
                              </button>
                              <button
                                type="button"
                                onClick={() => respondToJoinRequest(req.connection_id, true)}
                                className="px-2 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                              >
                                Accept
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {participants.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">
                    Roster is loading…
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-80 overflow-y-auto">
                    {participants.map((p) => {
                      const isYou = p.user_id === userId;
                      const shortId = p.user_id
                        ? `User ${p.user_id.slice(0, 8)}`
                        : "Unknown user";
                      const name = p.display_name || p.email || shortId;
                      return (
                        <li
                          key={p.connection_id}
                          className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                p.role === "host" ? "bg-green-500" : "bg-blue-400"
                              }`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-gray-900 font-medium truncate">
                                {name}
                                {isYou && (
                                  <span className="ml-1.5 text-xs text-gray-500 font-normal">
                                    (you)
                                  </span>
                                )}
                              </div>
                              {p.email && p.email !== name && (
                                <div className="text-xs text-gray-500 truncate">
                                  {p.email}
                                </div>
                              )}
                            </div>
                          </div>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                              p.role === "host"
                                ? "bg-green-100 text-green-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {p.role === "host" ? "Host" : "Participant"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* "Meeting already active" join prompt */}
          {pendingJoin && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Meeting already in progress
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  A meeting with ID{" "}
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-800">
                    {pendingJoin.meetingId}
                  </span>{" "}
                  is currently active. You'll join as a participant — you won't
                  share your screen, but you'll see the live transcript,
                  questions, and suggestions in real time.
                </p>
                {pendingJoin.participants?.length > 0 && (
                  <div className="mb-4 text-xs text-gray-500">
                    Currently in the room:{" "}
                    {pendingJoin.participants
                      .map((p) => {
                        const label =
                          p.display_name ||
                          p.email ||
                          (p.user_id ? `User ${p.user_id.slice(0, 8)}` : "anonymous");
                        return `${label}${p.role === "host" ? " (host)" : ""}`;
                      })
                      .join(", ")}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingJoin(null);
                      setShowMeetingModal(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Use different ID
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const target = pendingJoin.meetingId;
                      setPendingJoin(null);
                      joinAsParticipant(target);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Join as participant
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Waiting-for-host-approval screen (gate between Join button and meeting UI) */}
          {isPendingApproval && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
                <div className="relative w-16 h-16 mx-auto mb-5">
                  <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                  <div
                    className="absolute inset-0 border-4 border-transparent rounded-full animate-spin"
                    style={{ borderTopColor: "#6366f1" }}
                  ></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isInviteJoining ? "Joining meeting…" : "Waiting for the host to admit you…"}
                </h3>
                <p className="text-sm text-gray-600 mb-1">
                  Meeting ID{" "}
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-800">
                    {meetingId}
                  </span>
                </p>
                {!isInviteJoining && (
                  <p className="text-xs text-gray-400 mb-5">
                    Times out after {Math.floor(60)} seconds if there's no response.
                  </p>
                )}
                <button
                  type="button"
                  onClick={cancelPendingJoin}
                  className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  Cancel request
                </button>
              </div>
            </div>
          )}

          {/* "Your request was declined" message after host decline / timeout / meeting ended */}
          {declinedMessage && !isPendingApproval && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 text-center">
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {declinedMessage.startsWith("This meeting has ended")
                    ? "Meeting has ended"
                    : "Couldn't join the meeting"}
                </h3>
                <p className="text-sm text-gray-600 mb-5">{declinedMessage}</p>
                <button
                  type="button"
                  onClick={() => setDeclinedMessage(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {/* Waiting for host to START the meeting (invite link arrived before host) */}
          {isWaitingForHostToStart && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
                <div className="relative w-16 h-16 mx-auto mb-5">
                  <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                  <div
                    className="absolute inset-0 border-4 border-transparent rounded-full animate-spin"
                    style={{ borderTopColor: "#6366f1" }}
                  ></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Waiting for host to start…
                </h3>
                <p className="text-sm text-gray-600 mb-1">
                  Meeting ID{" "}
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-800">
                    {meetingId}
                  </span>
                </p>
                <p className="text-xs text-gray-400 mb-5">
                  You'll be connected automatically once the host starts.
                </p>
                <button
                  type="button"
                  onClick={cancelWaitingForHost}
                  className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Host-side pending-request toast stack (bottom right) */}
          {!isParticipantMode && pendingToasts.length > 0 && (
            <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2 max-w-sm">
              {pendingToasts.map((req) => {
                const name =
                  req.display_name ||
                  req.email ||
                  (req.user_id ? `User ${req.user_id.slice(0, 8)}` : "Someone");
                return (
                  <div
                    key={req.connection_id}
                    className="bg-white rounded-xl shadow-2xl border border-indigo-100 p-3 flex items-center gap-3"
                  >
                    <Users className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {name}
                      </p>
                      <p className="text-xs text-gray-500">wants to join</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => respondToJoinRequest(req.connection_id, false)}
                      className="px-3 py-1 text-xs font-medium text-gray-700 rounded-md hover:bg-gray-100 border border-gray-200"
                    >
                      Decline
                    </button>
                    <button
                      type="button"
                      onClick={() => respondToJoinRequest(req.connection_id, true)}
                      className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                    >
                      Accept
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Clear Session Confirmation Modal */}
          {showClearSessionModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Clear session?
                </h3>
                <p className="text-sm text-gray-600 mb-1">
                  This will discard all transcripts, suggestions, and follow-up
                  questions for the current session and start{" "}
                  <span className="font-medium text-orange-600">
                    {meetingId}-{sessionNumber + 1}
                  </span>
                  .
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  The meeting stays active and recording continues uninterrupted.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New company / prospect name (optional)
                </label>
                <input
                  type="text"
                  value={clearSessionProspectInput}
                  onChange={(e) => setClearSessionProspectInput(e.target.value)}
                  placeholder="Leave blank to clear without a new company"
                  disabled={clearingSession}
                  className="w-full mb-5 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowClearSessionModal(false);
                      setClearSessionProspectInput("");
                    }}
                    disabled={clearingSession}
                    className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => clearSession(clearSessionProspectInput.trim())}
                    disabled={clearingSession}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${clearingSession ? "animate-spin" : ""}`} />
                    {clearingSession ? "Clearing…" : "Clear Session"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* End Meeting Confirmation Modal */}
          {showEndConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  End meeting?
                </h3>
                <p className="text-sm text-gray-600 mb-5">
                  This will stop recording and finalize the transcript. You can
                  generate a summary afterwards.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEndConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEndConfirm(false);
                      stopCapture();
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <StopCircle className="w-4 h-4" />
                    End Meeting
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Meeting ID Modal */}
          <MeetingIdModal
            isOpen={showMeetingModal}
            onClose={() => setShowMeetingModal(false)}
            onConfirm={handleMeetingIdConfirm}
          />

          {/* Loading Overlay - Show while generating summary (host) or waiting for it (participant) */}
          {(isGeneratingSummary || participantWaitingOverlay) && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                backdropFilter: "blur(4px)",
              }}
            >
              <div
                style={{
                  background: "white",
                  padding: "40px 60px",
                  borderRadius: "16px",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                  textAlign: "center",
                  maxWidth: "400px",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #667eea",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 20px",
                  }}
                />
                <h3
                  style={{
                    margin: "0 0 10px 0",
                    color: "#333",
                    fontSize: "1.5em",
                    fontWeight: 600,
                  }}
                >
                  {participantWaitingOverlay ? "Meeting Ended" : "Generating Summary"}
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "#666",
                    fontSize: "1em",
                    lineHeight: 1.5,
                  }}
                >
                  {participantWaitingOverlay
                    ? "The host has ended the meeting. Your summary is being prepared — you'll be redirected shortly."
                    : "Please wait while we analyze the meeting transcripts and generate your summary, notes, and action items..."}
                </p>
              </div>
              <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
            </div>
          )}
        </div>
      </div>

      {/* Host disconnected overlay — shown to participants on abnormal host dropout */}
      {hostDisconnectedOverlay && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "40px 52px",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
              maxWidth: "420px",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 9.68 19.79 19.79 0 0 1 1.36 1a2 2 0 0 1 2-2.18H6a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.08 6.62"/>
                <line x1="23" y1="1" x2="1" y2="23"/>
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 10px 0",
                color: "#111",
                fontSize: "1.4em",
                fontWeight: 700,
              }}
            >
              Host Disconnected
            </h3>
            <p
              style={{
                margin: "0 0 28px 0",
                color: "#666",
                fontSize: "0.95em",
                lineHeight: 1.6,
              }}
            >
              The host's connection was lost and the meeting has been closed.
              Any recorded transcript has been saved.
            </p>
            <button
              onClick={() => {
                setHostDisconnectedOverlay(false);
                navigate("/meetings");
              }}
              style={{
                padding: "10px 28px",
                background: "#667eea",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "0.95em",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Back to Meetings
            </button>
          </div>
        </div>
      )}

      {/* Leave-meeting confirmation — fires when host clicks a header nav link */}
      {showLeaveModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "420px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "#FEF2F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <AlertCircle size={28} color="#EF4444" />
            </div>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "10px",
              }}
            >
              Leave active meeting?
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#6B7280",
                marginBottom: "28px",
                lineHeight: "1.6",
              }}
            >
              Navigating away will end the meeting for all participants. This
              cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => { setShowLeaveModal(false); setPendingNavPath(null); }}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  background: "#fff",
                  color: "#374151",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Stay in meeting
              </button>
              <button
                onClick={() => { navigate(pendingNavPath); setShowLeaveModal(false); }}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#EF4444",
                  color: "#fff",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewMeeting;
